import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Tab,
    Tabs,
    Divider,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Alert,
    CircularProgress,
} from '@mui/material'
import {
    Add,
    Edit,
    Delete,
    Visibility,
    ContentCopy,
    Code,
    DragIndicator,
    TextFields,
    Email,
    Phone,
    Notes,
    List,
    CheckBox,
    RadioButtonChecked,
    CalendarToday,
    Numbers,
    AttachFile,
    PersonAdd,
    People,
    AccessTime,
    Security,
    Repeat,
    WhatsApp,
    Settings,
    Api,
    Forum,
    Label,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { formsApi } from '@api/forms'
import axios from '@api/axios'
import { formUsersApi } from '@api/formUsers'
import { uploadApi } from '@api/upload'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import { toast } from 'react-toastify'
import Loading from '@components/Common/Loading'
import ConfirmDialog from '@components/Common/ConfirmDialog'
import WhatsAppSettings from '@components/WhatsAppSettings'
import LiveChatSettings from '@components/LiveChatSettings'
import FormApiSettings from '@components/FormApiSettings'

const FIELD_TYPES = [
    { type: 'text', label: 'Text', icon: <TextFields /> },
    { type: 'email', label: 'Email', icon: <Email /> },
    { type: 'phone', label: 'Phone', icon: <Phone /> },
    { type: 'number', label: 'Number', icon: <Numbers /> },
    { type: 'textarea', label: 'Text Area', icon: <Notes /> },
    { type: 'select', label: 'Dropdown', icon: <List /> },
    { type: 'radio', label: 'Radio Buttons', icon: <RadioButtonChecked /> },
    { type: 'checkbox', label: 'Checkbox', icon: <CheckBox /> },
    { type: 'date', label: 'Date', icon: <CalendarToday /> },
    { type: 'time', label: 'Time', icon: <AccessTime /> },
    { type: 'file', label: 'File Upload', icon: <AttachFile /> },
    { type: 'captcha', label: 'Captcha', icon: <Security /> },
    { type: 'repeater', label: 'Repeater Group', icon: <Repeat /> },
]

const WIDTH_OPTIONS = [
    { value: '100', label: 'Full Width (100%)' },
    { value: '75', label: 'Three Quarters (75%)' },
    { value: '50', label: 'Half (50%)' },
    { value: '33', label: 'Third (33%)' },
    { value: '25', label: 'Quarter (25%)' },
]

// Pre-built form templates
const FORM_TEMPLATES = [
    {
        id: 'contact',
        name: 'Contact Form',
        name_ar: 'نموذج اتصل بنا',
        icon: '📧',
        description: 'Simple contact form with name, email, phone, and message',
        data: {
            title: 'Contact Us',
            title_ar: 'اتصل بنا',
            description: 'We\'d love to hear from you. Send us a message.',
            description_ar: 'نود أن نسمع منك. أرسل لنا رسالة.',
            submission_prefix: 'CONTACT',
            // Success & Redirect
            success_message_en: 'Thank you for contacting us! We will respond within 24-48 hours.',
            success_message_ar: 'شكراً لتواصلك معنا! سنرد عليك خلال 24-48 ساعة.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Contact Us',
            header_title_ar: 'اتصل بنا',
            header_subtitle_en: 'Send us your inquiry',
            header_subtitle_ar: 'أرسل لنا استفسارك',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear {name},\n\nThank you for contacting us. We have received your message (Ref: {submission_id}).\n\nOur team will review your inquiry and respond within 24-48 business hours.\n\nBest regards,\nCustomer Service Team',
            email_acknowledgment_ar: 'عزيزي {name}،\n\nشكراً لتواصلك معنا. لقد استلمنا رسالتك (المرجع: {submission_id}).\n\nسيقوم فريقنا بمراجعة استفسارك والرد عليك خلال 24-48 ساعة عمل.\n\nمع أطيب التحيات،\nفريق خدمة العملاء',
            email_followup_en: 'Dear {name},\n\nThis is a follow-up regarding your inquiry (Ref: {submission_id}).\n\nWe are currently reviewing your message and will provide you with a detailed response soon.\n\nBest regards,\nCustomer Service Team',
            email_followup_ar: 'عزيزي {name}،\n\nهذه متابعة بخصوص استفسارك (المرجع: {submission_id}).\n\nنحن نراجع رسالتك حالياً وسنوافيك بالرد المفصل قريباً.\n\nمع أطيب التحيات،\nفريق خدمة العملاء',
            email_resolution_en: 'Dear {name},\n\nWe are pleased to inform you that your inquiry (Ref: {submission_id}) has been resolved.\n\nThank you for reaching out to us.\n\nBest regards,\nCustomer Service Team',
            email_resolution_ar: 'عزيزي {name}،\n\nيسعدنا إبلاغك بأن استفسارك (المرجع: {submission_id}) قد تم حله.\n\nشكراً لتواصلك معنا.\n\nمع أطيب التحيات،\nفريق خدمة العملاء',
            fields: [
                { id: 'full_name', type: 'text', label_en: 'Full Name', label_ar: 'الاسم الكامل', required: true, width: '50' },
                { id: 'email', type: 'email', label_en: 'Email Address', label_ar: 'البريد الإلكتروني', required: true, width: '50' },
                { id: 'phone', type: 'phone', label_en: 'Phone Number', label_ar: 'رقم الهاتف', required: false, width: '50' },
                {
                    id: 'subject', type: 'select', label_en: 'Subject', label_ar: 'الموضوع', required: true, width: '50', options: [
                        { value: 'general', label_en: 'General Inquiry', label_ar: 'استفسار عام' },
                        { value: 'support', label_en: 'Support', label_ar: 'الدعم' },
                        { value: 'feedback', label_en: 'Feedback', label_ar: 'ملاحظات' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },
                { id: 'message', type: 'textarea', label_en: 'Message', label_ar: 'الرسالة', required: true, width: '100' },
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    },
    {
        id: 'complaint',
        name: 'Complaint Form',
        name_ar: 'نموذج الشكوى',
        icon: '📋',
        description: 'Formal complaint submission with detailed information',
        data: {
            title: 'Submit a Complaint',
            title_ar: 'تقديم شكوى',
            description: 'Please provide detailed information about your complaint.',
            description_ar: 'يرجى تقديم معلومات مفصلة عن شكواك.',
            submission_prefix: 'COMPLAINT',
            // Success & Redirect
            success_message_en: 'Your complaint has been submitted successfully. We will review it and get back to you within 5 business days.',
            success_message_ar: 'تم تقديم شكواك بنجاح. سنقوم بمراجعتها والرد عليك خلال 5 أيام عمل.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Complaint Submission',
            header_title_ar: 'تقديم شكوى',
            header_subtitle_en: 'Ministry of Justice',
            header_subtitle_ar: 'وزارة العدل',
            // Email Header Settings
            email_header_enabled: true,
            email_header_title_en: 'Ministry of Justice',
            email_header_title_ar: 'وزارة العدل',
            email_header_subtitle_en: 'Complaint Management System',
            email_header_subtitle_ar: 'نظام إدارة الشكاوى',
            email_header_color: '#1a3a6b',
            email_header_gradient_end: '#0d2247',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear {name},\n\nWe have received your complaint (Ref: {submission_id}) and want to assure you that your concerns are important to us.\n\nOur team will thoroughly investigate the matter and provide you with a response within 5 business days.\n\nThank you for bringing this to our attention.\n\nBest regards,\nComplaint Management Team',
            email_acknowledgment_ar: 'عزيزي {name}،\n\nلقد استلمنا شكواك (المرجع: {submission_id}) ونؤكد لك أن مخاوفك مهمة لنا.\n\nسيقوم فريقنا بالتحقيق الشامل في الأمر وتزويدك بالرد خلال 5 أيام عمل.\n\nشكراً لإبلاغنا بهذا الأمر.\n\nمع أطيب التحيات،\nفريق إدارة الشكاوى',
            email_followup_en: 'Dear {name},\n\nThis is an update regarding your complaint (Ref: {submission_id}).\n\nWe are actively investigating your complaint and will provide you with a detailed response as soon as possible.\n\nWe appreciate your patience.\n\nBest regards,\nComplaint Management Team',
            email_followup_ar: 'عزيزي {name}،\n\nهذا تحديث بخصوص شكواك (المرجع: {submission_id}).\n\nنحن نحقق بفعالية في شكواك وسنوافيك بالرد المفصل في أقرب وقت ممكن.\n\nنقدر صبرك.\n\nمع أطيب التحيات،\nفريق إدارة الشكاوى',
            email_resolution_en: 'Dear {name},\n\nWe are writing to inform you that your complaint (Ref: {submission_id}) has been reviewed and resolved.\n\nThank you for your feedback, which helps us improve our services.\n\nIf you have any further concerns, please do not hesitate to contact us.\n\nBest regards,\nComplaint Management Team',
            email_resolution_ar: 'عزيزي {name}،\n\nنود إبلاغك بأن شكواك (المرجع: {submission_id}) قد تمت مراجعتها وحلها.\n\nشكراً لملاحظاتك التي تساعدنا على تحسين خدماتنا.\n\nإذا كانت لديك أي مخاوف أخرى، يرجى عدم التردد في الاتصال بنا.\n\nمع أطيب التحيات،\nفريق إدارة الشكاوى',
            fields: [
                { id: 'full_name', type: 'text', label_en: 'Full Name', label_ar: 'الاسم الكامل', required: true, width: '50' },
                { id: 'national_id', type: 'text', label_en: 'National ID / Iqama', label_ar: 'رقم الهوية / الإقامة', required: true, width: '50' },
                { id: 'email', type: 'email', label_en: 'Email Address', label_ar: 'البريد الإلكتروني', required: true, require_verification: true, width: '50' },
                { id: 'phone', type: 'phone', label_en: 'Phone Number', label_ar: 'رقم الهاتف', required: true, width: '50' },
                {
                    id: 'complaint_type',
                    type: 'select',
                    label_en: 'Type of Complaint',
                    label_ar: 'نوع الشكوى',
                    required: true,
                    width: '50',
                    options: [
                        { value: 'service', label_en: 'Service Quality', label_ar: 'جودة الخدمة' },
                        { value: 'facility', label_en: 'Facility', label_ar: 'المرافق' },
                        { value: 'safety', label_en: 'Safety', label_ar: 'السلامة' },
                        { value: 'staff', label_en: 'Staff Behavior', label_ar: 'سلوك الموظفين' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },
                {
                    id: 'location',
                    type: 'select',
                    label_en: 'Location',
                    label_ar: 'الموقع',
                    required: true,
                    width: '50',
                    options: [
                        { value: 'airport', label_en: 'Airport', label_ar: 'المطار' },
                        { value: 'terminal', label_en: 'Terminal', label_ar: 'الصالة' },
                        { value: 'website', label_en: 'Website', label_ar: 'الموقع الإلكتروني' },
                        { value: 'customer_service', label_en: 'Customer Service', label_ar: 'خدمة العملاء' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },
                { id: 'incident_date', type: 'date', label_en: 'Incident Date', label_ar: 'تاريخ الحادثة', required: true, width: '50' },
                { id: 'incident_time', type: 'time', label_en: 'Incident Time', label_ar: 'وقت الحادثة', required: false, width: '50' },
                { id: 'description', type: 'textarea', label_en: 'Complaint Description', label_ar: 'وصف الشكوى', required: true, width: '100', min_length: 50 },
                { id: 'attachments', type: 'file', label_en: 'Supporting Documents', label_ar: 'المستندات الداعمة', required: false, width: '100' },
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    },
    {
        id: 'feedback',
        name: 'Feedback Form',
        name_ar: 'نموذج الملاحظات',
        icon: '⭐',
        description: 'Customer satisfaction and feedback survey',
        data: {
            title: 'Share Your Feedback',
            title_ar: 'شاركنا رأيك',
            description: 'Your feedback helps us improve our services.',
            description_ar: 'ملاحظاتك تساعدنا على تحسين خدماتنا.',
            submission_prefix: 'FEEDBACK',
            // Success & Redirect
            success_message_en: 'Thank you for your valuable feedback! Your opinion matters to us.',
            success_message_ar: 'شكراً لملاحظاتك القيمة! رأيك مهم لنا.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Customer Feedback',
            header_title_ar: 'ملاحظات العملاء',
            header_subtitle_en: 'Help us serve you better',
            header_subtitle_ar: 'ساعدنا لخدمتك بشكل أفضل',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear {name},\n\nThank you for taking the time to share your feedback (Ref: {submission_id}).\n\nYour opinion is valuable to us and helps us continuously improve our services.\n\nBest regards,\nCustomer Experience Team',
            email_acknowledgment_ar: 'عزيزي {name}،\n\nشكراً لتخصيص وقتك لمشاركة ملاحظاتك (المرجع: {submission_id}).\n\nرأيك قيم لنا ويساعدنا على تحسين خدماتنا باستمرار.\n\nمع أطيب التحيات،\nفريق تجربة العملاء',
            email_followup_en: 'Dear {name},\n\nWe wanted to follow up on your feedback (Ref: {submission_id}).\n\nYour suggestions are being reviewed by our team.\n\nBest regards,\nCustomer Experience Team',
            email_followup_ar: 'عزيزي {name}،\n\nنود المتابعة بخصوص ملاحظاتك (المرجع: {submission_id}).\n\nيتم مراجعة اقتراحاتك من قبل فريقنا.\n\nمع أطيب التحيات،\nفريق تجربة العملاء',
            email_resolution_en: 'Dear {name},\n\nThank you for your feedback (Ref: {submission_id}).\n\nWe have implemented improvements based on customer suggestions like yours.\n\nBest regards,\nCustomer Experience Team',
            email_resolution_ar: 'عزيزي {name}،\n\nشكراً لملاحظاتك (المرجع: {submission_id}).\n\nلقد قمنا بتنفيذ تحسينات بناءً على اقتراحات العملاء مثلك.\n\nمع أطيب التحيات،\nفريق تجربة العملاء',
            fields: [
                { id: 'name', type: 'text', label_en: 'Your Name', label_ar: 'اسمك', required: false, width: '50' },
                { id: 'email', type: 'email', label_en: 'Email (optional)', label_ar: 'البريد الإلكتروني (اختياري)', required: false, width: '50' },
                {
                    id: 'rating', type: 'radio', label_en: 'Overall Rating', label_ar: 'التقييم العام', required: true, width: '100', options: [
                        { value: 'excellent', label_en: 'Excellent', label_ar: 'ممتاز' },
                        { value: 'good', label_en: 'Good', label_ar: 'جيد' },
                        { value: 'average', label_en: 'Average', label_ar: 'متوسط' },
                        { value: 'poor', label_en: 'Poor', label_ar: 'ضعيف' }
                    ]
                },
                {
                    id: 'service_area', type: 'select', label_en: 'Service Area', label_ar: 'مجال الخدمة', required: true, width: '100', options: [
                        { value: 'customer_service', label_en: 'Customer Service', label_ar: 'خدمة العملاء' },
                        { value: 'product_quality', label_en: 'Product Quality', label_ar: 'جودة المنتج' },
                        { value: 'website', label_en: 'Website', label_ar: 'الموقع الإلكتروني' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },
                { id: 'comments', type: 'textarea', label_en: 'Your Comments', label_ar: 'تعليقاتك', required: true, width: '100' },
                {
                    id: 'recommend', type: 'radio', label_en: 'Would you recommend us?', label_ar: 'هل توصي بنا؟', required: true, width: '100', options: [
                        { value: 'yes', label_en: 'Yes', label_ar: 'نعم' },
                        { value: 'no', label_en: 'No', label_ar: 'لا' },
                        { value: 'maybe', label_en: 'Maybe', label_ar: 'ربما' }
                    ]
                },
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    },
    {
        id: 'job_application',
        name: 'Job Application',
        name_ar: 'طلب توظيف',
        icon: '💼',
        description: 'Employment application with resume upload',
        data: {
            title: 'Job Application',
            title_ar: 'طلب توظيف',
            description: 'Apply for open positions at our organization.',
            description_ar: 'تقدم للوظائف المتاحة في مؤسستنا.',
            submission_prefix: 'JOB',
            // Success & Redirect
            success_message_en: 'Your application has been submitted successfully. We will review it and contact you if your qualifications match our requirements.',
            success_message_ar: 'تم تقديم طلبك بنجاح. سنقوم بمراجعته والتواصل معك إذا تطابقت مؤهلاتك مع متطلباتنا.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Career Opportunities',
            header_title_ar: 'فرص العمل',
            header_subtitle_en: 'Join our team',
            header_subtitle_ar: 'انضم إلى فريقنا',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear {name},\n\nThank you for your interest in joining our team. We have received your application (Ref: {submission_id}).\n\nOur HR team will review your qualifications and contact you if there is a suitable match.\n\nBest regards,\nHuman Resources Department',
            email_acknowledgment_ar: 'عزيزي {name}،\n\nشكراً لاهتمامك بالانضمام إلى فريقنا. لقد استلمنا طلبك (المرجع: {submission_id}).\n\nسيقوم فريق الموارد البشرية بمراجعة مؤهلاتك والتواصل معك إذا كان هناك تطابق مناسب.\n\nمع أطيب التحيات،\nإدارة الموارد البشرية',
            email_followup_en: 'Dear {name},\n\nThis is an update regarding your job application (Ref: {submission_id}).\n\nYour application is currently under review. We will contact you soon with further information.\n\nBest regards,\nHuman Resources Department',
            email_followup_ar: 'عزيزي {name}،\n\nهذا تحديث بخصوص طلب التوظيف الخاص بك (المرجع: {submission_id}).\n\nطلبك قيد المراجعة حالياً. سنتصل بك قريباً بمزيد من المعلومات.\n\nمع أطيب التحيات،\nإدارة الموارد البشرية',
            email_resolution_en: 'Dear {name},\n\nThank you for your application (Ref: {submission_id}).\n\nAfter careful consideration, we wanted to update you on the status of your application.\n\nBest regards,\nHuman Resources Department',
            email_resolution_ar: 'عزيزي {name}،\n\nشكراً لطلبك (المرجع: {submission_id}).\n\nبعد دراسة متأنية، نود إطلاعك على حالة طلبك.\n\nمع أطيب التحيات،\nإدارة الموارد البشرية',
            fields: [
                { id: 'full_name', type: 'text', label_en: 'Full Name', label_ar: 'الاسم الكامل', required: true, width: '50' },
                { id: 'email', type: 'email', label_en: 'Email Address', label_ar: 'البريد الإلكتروني', required: true, width: '50' },
                { id: 'phone', type: 'phone', label_en: 'Phone Number', label_ar: 'رقم الهاتف', required: true, width: '50' },
                { id: 'position', type: 'text', label_en: 'Position Applied For', label_ar: 'الوظيفة المتقدم لها', required: true, width: '50' },
                {
                    id: 'experience', type: 'select', label_en: 'Years of Experience', label_ar: 'سنوات الخبرة', required: true, width: '50', options: [
                        { value: '0-1', label_en: '0-1 years', label_ar: '0-1 سنة' },
                        { value: '2-3', label_en: '2-3 years', label_ar: '2-3 سنوات' },
                        { value: '4-6', label_en: '4-6 years', label_ar: '4-6 سنوات' },
                        { value: '7+', label_en: '7+ years', label_ar: '7+ سنوات' }
                    ]
                },
                {
                    id: 'education', type: 'select', label_en: 'Highest Education', label_ar: 'أعلى مؤهل', required: true, width: '50', options: [
                        { value: 'high_school', label_en: 'High School', label_ar: 'ثانوية' },
                        { value: 'bachelor', label_en: 'Bachelor', label_ar: 'بكالوريوس' },
                        { value: 'master', label_en: 'Master', label_ar: 'ماجستير' },
                        { value: 'phd', label_en: 'PhD', label_ar: 'دكتوراه' }
                    ]
                },
                { id: 'cover_letter', type: 'textarea', label_en: 'Cover Letter', label_ar: 'خطاب التقديم', required: false, width: '100' },
                { id: 'resume', type: 'file', label_en: 'Upload Resume (PDF)', label_ar: 'رفع السيرة الذاتية', required: true, width: '100' },
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    },
    {
        id: 'survey',
        name: 'Survey Form',
        name_ar: 'نموذج استبيان',
        icon: '📊',
        description: 'General purpose survey template',
        data: {
            title: 'Survey',
            title_ar: 'استبيان',
            description: 'Please take a moment to complete this survey.',
            description_ar: 'يرجى تخصيص دقيقة لإكمال هذا الاستبيان.',
            submission_prefix: 'SURVEY',
            // Success & Redirect
            success_message_en: 'Thank you for completing the survey! Your responses have been recorded.',
            success_message_ar: 'شكراً لإكمال الاستبيان! تم تسجيل إجاباتك.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Customer Survey',
            header_title_ar: 'استبيان العملاء',
            header_subtitle_en: 'Your opinion matters',
            header_subtitle_ar: 'رأيك مهم',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear Participant,\n\nThank you for completing our survey (Ref: {submission_id}).\n\nYour responses will help us improve our services.\n\nBest regards,\nResearch Team',
            email_acknowledgment_ar: 'عزيزي المشارك،\n\nشكراً لإكمال استبياننا (المرجع: {submission_id}).\n\nستساعدنا إجاباتك على تحسين خدماتنا.\n\nمع أطيب التحيات،\nفريق البحث',
            email_followup_en: 'Dear Participant,\n\nThank you for your participation in our survey (Ref: {submission_id}).\n\nWe are analyzing the responses and will share the results soon.\n\nBest regards,\nResearch Team',
            email_followup_ar: 'عزيزي المشارك،\n\nشكراً لمشاركتك في استبياننا (المرجع: {submission_id}).\n\nنحن نحلل الردود وسنشارك النتائج قريباً.\n\nمع أطيب التحيات،\nفريق البحث',
            email_resolution_en: 'Dear Participant,\n\nThank you for your participation in our survey (Ref: {submission_id}).\n\nThe survey results have been compiled and improvements are being implemented.\n\nBest regards,\nResearch Team',
            email_resolution_ar: 'عزيزي المشارك،\n\nشكراً لمشاركتك في استبياننا (المرجع: {submission_id}).\n\nتم جمع نتائج الاستبيان ويتم تنفيذ التحسينات.\n\nمع أطيب التحيات،\nفريق البحث',
            fields: [
                {
                    id: 'age_group', type: 'select', label_en: 'Age Group', label_ar: 'الفئة العمرية', required: true, width: '50', options: [
                        { value: '18-24', label_en: '18-24', label_ar: '18-24' },
                        { value: '25-34', label_en: '25-34', label_ar: '25-34' },
                        { value: '35-44', label_en: '35-44', label_ar: '35-44' },
                        { value: '45-54', label_en: '45-54', label_ar: '45-54' },
                        { value: '55+', label_en: '55+', label_ar: '55+' }
                    ]
                },
                {
                    id: 'gender', type: 'radio', label_en: 'Gender', label_ar: 'الجنس', required: false, width: '50', options: [
                        { value: 'male', label_en: 'Male', label_ar: 'ذكر' },
                        { value: 'female', label_en: 'Female', label_ar: 'أنثى' },
                        { value: 'prefer_not', label_en: 'Prefer not to say', label_ar: 'أفضل عدم الإجابة' }
                    ]
                },
                {
                    id: 'question1', type: 'radio', label_en: 'How satisfied are you?', label_ar: 'ما مدى رضاك؟', required: true, width: '100', options: [
                        { value: 'very_satisfied', label_en: 'Very Satisfied', label_ar: 'راضٍ جداً' },
                        { value: 'satisfied', label_en: 'Satisfied', label_ar: 'راضٍ' },
                        { value: 'neutral', label_en: 'Neutral', label_ar: 'محايد' },
                        { value: 'dissatisfied', label_en: 'Dissatisfied', label_ar: 'غير راضٍ' }
                    ]
                },
                {
                    id: 'question2', type: 'checkbox', label_en: 'Which services did you use?', label_ar: 'أي الخدمات استخدمت؟', required: true, width: '100', options: [
                        { value: 'service_a', label_en: 'Service A', label_ar: 'خدمة أ' },
                        { value: 'service_b', label_en: 'Service B', label_ar: 'خدمة ب' },
                        { value: 'service_c', label_en: 'Service C', label_ar: 'خدمة ج' }
                    ]
                },
                { id: 'suggestions', type: 'textarea', label_en: 'Suggestions for improvement', label_ar: 'اقتراحات للتحسين', required: false, width: '100' },
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    },
    {
        id: 'delegation',
        name: 'Delegation Reception/Farewell',
        name_ar: 'نموذج استقبال وتوديع الوفود',
        icon: '✈️',
        description: 'Form for coordinating official delegation reception and farewell procedures',
        data: {
            title: 'Delegation Reception & Farewell Form',
            title_ar: 'نموذج استقبال وتوديع الوفود',
            description: 'Form for coordinating official delegation arrival and departure procedures at the airport.',
            description_ar: 'نموذج تنسيق واستقبال الوفود الرسمية في المطار.',
            submission_prefix: 'DELEGATION',
            // Success & Redirect
            success_message_en: 'Your delegation coordination request has been submitted successfully. We will review it and contact you for confirmation.',
            success_message_ar: 'تم تقديم طلب تنسيق الوفد بنجاح. سنقوم بمراجعته والتواصل معكم للتأكيد.',
            // Header Settings
            header_enabled: true,
            header_title_en: 'Delegation Reception & Farewell',
            header_title_ar: 'استقبال وتوديع الوفود',
            header_subtitle_en: 'Ministry of Justice',
            header_subtitle_ar: 'وزارة العدل',
            // Email Header Settings
            email_header_enabled: true,
            email_header_title_en: 'Ministry of Justice',
            email_header_title_ar: 'وزارة العدل',
            email_header_subtitle_en: 'Delegation Coordination System',
            email_header_subtitle_ar: 'نظام تنسيق الوفود',
            email_header_color: '#1a3a6b',
            email_header_gradient_end: '#0d2247',
            // Style Settings
            primary_color: '#1a3a6b',
            header_gradient_end: '#0d2247',
            background_color: '#1a3a6b',
            button_color: '#1a3a6b',
            button_hover_color: '#246b57',
            button_text_color: '#ffffff',
            // Email Templates
            email_acknowledgment_en: 'Dear {name},\n\nWe have received your delegation coordination request (Ref: {submission_id}).\n\nOur team will review the details and contact you to confirm the arrangements.\n\nBest regards,\nProtocol & Coordination Team',
            email_acknowledgment_ar: 'عزيزي {name}،\n\nلقد استلمنا طلب تنسيق الوفد الخاص بكم (المرجع: {submission_id}).\n\nسيقوم فريقنا بمراجعة التفاصيل والتواصل معكم لتأكيد الترتيبات.\n\nمع أطيب التحيات،\nفريق البروتوكول والتنسيق',
            email_followup_en: 'Dear {name},\n\nThis is an update regarding your delegation coordination request (Ref: {submission_id}).\n\nWe are finalizing the arrangements and will confirm the details shortly.\n\nBest regards,\nProtocol & Coordination Team',
            email_followup_ar: 'عزيزي {name}،\n\nهذا تحديث بخصوص طلب تنسيق الوفد (المرجع: {submission_id}).\n\nنحن ننهي الترتيبات وسنؤكد التفاصيل قريباً.\n\nمع أطيب التحيات،\nفريق البروتوكول والتنسيق',
            email_resolution_en: 'Dear {name},\n\nYour delegation coordination request (Ref: {submission_id}) has been confirmed.\n\nAll arrangements are in place. Please contact us if you need any changes.\n\nBest regards,\nProtocol & Coordination Team',
            email_resolution_ar: 'عزيزي {name}،\n\nتم تأكيد طلب تنسيق الوفد (المرجع: {submission_id}).\n\nجميع الترتيبات جاهزة. يرجى التواصل معنا في حال الحاجة لأي تغييرات.\n\nمع أطيب التحيات،\nفريق البروتوكول والتنسيق',
            fields: [
                // Section: Government Entity
                {
                    id: 'government_entity',
                    type: 'select',
                    label_en: 'Government Entity',
                    label_ar: 'الجهة الحكومية',
                    required: true,
                    width: '50',
                    options: [
                        { value: 'presidency', label_en: 'Presidency', label_ar: 'رئاسة الجمهورية' },
                        { value: 'prime_ministry', label_en: 'Prime Ministry', label_ar: 'رئاسة مجلس الوزراء' },
                        { value: 'foreign_affairs', label_en: 'Ministry of Foreign Affairs', label_ar: 'وزارة الخارجية' },
                        { value: 'interior', label_en: 'Ministry of Interior', label_ar: 'وزارة الداخلية' },
                        { value: 'defense', label_en: 'Ministry of Defense', label_ar: 'وزارة الدفاع' },
                        { value: 'transport', label_en: 'Ministry of Transport', label_ar: 'وزارة النقل' },
                        { value: 'finance', label_en: 'Ministry of Finance', label_ar: 'وزارة المالية' },
                        { value: 'economy', label_en: 'Ministry of Economy', label_ar: 'وزارة الاقتصاد' },
                        { value: 'tourism', label_en: 'Ministry of Tourism', label_ar: 'وزارة السياحة' },
                        { value: 'health', label_en: 'Ministry of Health', label_ar: 'وزارة الصحة' },
                        { value: 'education', label_en: 'Ministry of Education', label_ar: 'وزارة التربية' },
                        { value: 'higher_education', label_en: 'Ministry of Higher Education', label_ar: 'وزارة التعليم العالي' },
                        { value: 'justice', label_en: 'Ministry of Justice', label_ar: 'وزارة العدل' },
                        { value: 'culture', label_en: 'Ministry of Culture', label_ar: 'وزارة الثقافة' },
                        { value: 'information', label_en: 'Ministry of Information', label_ar: 'وزارة الإعلام' },
                        { value: 'oil', label_en: 'Ministry of Oil', label_ar: 'وزارة النفط' },
                        { value: 'electricity', label_en: 'Ministry of Electricity', label_ar: 'وزارة الكهرباء' },
                        { value: 'water', label_en: 'Ministry of Water Resources', label_ar: 'وزارة الموارد المائية' },
                        { value: 'agriculture', label_en: 'Ministry of Agriculture', label_ar: 'وزارة الزراعة' },
                        { value: 'social_affairs', label_en: 'Ministry of Social Affairs', label_ar: 'وزارة الشؤون الاجتماعية' },
                        { value: 'customs', label_en: 'General Directorate of Customs', label_ar: 'الهيئة العامة للجمارك' },
                        { value: 'civil_aviation', label_en: 'Ministry of Justice', label_ar: 'وزارة العدل' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },
                { id: 'other_entity', type: 'text', label_en: 'Other Entity (if applicable)', label_ar: 'جهة أخرى (إن وجدت)', required: false, width: '50' },

                // Section: Flight Information
                {
                    id: 'flight_status',
                    type: 'radio',
                    label_en: 'Flight Status',
                    label_ar: 'حالة الرحلة',
                    required: true,
                    width: '100',
                    options: [
                        { value: 'arrival', label_en: 'Arrival', label_ar: 'وصول' },
                        { value: 'departure', label_en: 'Departure', label_ar: 'مغادرة' }
                    ]
                },
                {
                    id: 'airline',
                    type: 'select',
                    label_en: 'Airline',
                    label_ar: 'شركة الطيران',
                    required: true,
                    width: '50',
                    options: [
                        { value: 'syrian_air', label_en: 'Syrian Air (SY)', label_ar: 'الخطوط الجوية السورية (SY)' },
                        { value: 'cham_wings', label_en: 'Cham Wings (6Q)', label_ar: 'أجنحة الشام (6Q)' },
                        { value: 'fly_damascus', label_en: 'Fly Damascus', label_ar: 'فلاي دمشق' },
                        { value: 'other', label_en: 'Other Airline', label_ar: 'شركة أخرى' }
                    ]
                },
                { id: 'flight_number', type: 'text', label_en: 'Flight Number', label_ar: 'رقم الرحلة', required: true, width: '50', placeholder_en: 'e.g., SY101', placeholder_ar: 'مثال: SY101' },
                { id: 'flight_date', type: 'date', label_en: 'Flight Date', label_ar: 'تاريخ الرحلة', required: true, width: '50' },
                { id: 'flight_time', type: 'time', label_en: 'Flight Time', label_ar: 'توقيت الرحلة', required: true, width: '50' },

                // Origin Airport - Dynamic label based on flight_status, hidden for Departure
                {
                    id: 'origin_airport',
                    type: 'text',
                    label_en: 'Arrival (Name of the airport of origin)',
                    label_ar: 'جهة القدوم (اسم المطار القادم منه)',
                    required: true,
                    width: '50',
                    // Conditional visibility: only show for Arrival
                    conditionalVisibility: {
                        field: 'flight_status',
                        showWhen: 'arrival'
                    }
                },

                // Destination/Departure Airport - Dynamic label based on flight_status
                {
                    id: 'destination_airport',
                    type: 'select',
                    label_en: 'Destination Airport',
                    label_ar: 'جهة الوصول (المطار)',
                    required: true,
                    width: '50',
                    options: [
                        { value: 'damascus', label_en: 'Damascus International Airport', label_ar: 'مطار دمشق الدولي' },
                        { value: 'aleppo', label_en: 'Aleppo International Airport', label_ar: 'مطار حلب الدولي' },
                        { value: 'latakia', label_en: 'Latakia International Airport', label_ar: 'مطار اللاذقية الدولي' },
                        { value: 'qamishli', label_en: 'Qamishli International Airport', label_ar: 'مطار القامشلي الدولي' },
                        { value: 'deir_ezzor', label_en: 'Deir Ezzor Airport', label_ar: 'مطار دير الزور' }
                    ],
                    // Dynamic labels based on flight_status selection
                    conditionalLabels: {
                        field: 'flight_status',
                        values: {
                            arrival: { label_en: 'Destination Airport', label_ar: 'جهة الوصول (المطار)' },
                            departure: { label_en: 'Departure Airport', label_ar: 'جهة المغادرة (المطار)' }
                        }
                    }
                },

                // Section: Delegation Information
                { id: 'delegation_count', type: 'number', label_en: 'Number of Delegation Members', label_ar: 'عدد أفراد الوفد', required: true, width: '100' },
                { id: 'head_name', type: 'text', label_en: 'Delegation Head - Full Name', label_ar: 'رئيس الوفد - الاسم الثلاثي', required: true, width: '50' },

                // Official Title - Changed to Select with common titles + Other option
                {
                    id: 'head_title',
                    type: 'select',
                    label_en: 'Delegation Head - Official Title',
                    label_ar: 'رئيس الوفد - الصفة الرسمية',
                    required: true,
                    width: '50',
                    allowOther: true,
                    otherLabel_en: 'Please specify',
                    otherLabel_ar: 'يرجى التحديد',
                    options: [
                        { value: 'minister', label_en: 'Minister', label_ar: 'وزير' },
                        { value: 'deputy_minister', label_en: 'Deputy Minister', label_ar: 'نائب وزير' },
                        { value: 'director_general', label_en: 'Director General', label_ar: 'مدير عام' },
                        { value: 'chairman', label_en: 'Chairman of the Board', label_ar: 'رئيس مجلس إدارة' },
                        { value: 'ceo', label_en: 'CEO', label_ar: 'رئيس تنفيذي' },
                        { value: 'advisor', label_en: 'Advisor', label_ar: 'مستشار' },
                        { value: 'ambassador', label_en: 'Ambassador', label_ar: 'سفير' },
                        { value: 'delegation_head', label_en: 'Delegation Head', label_ar: 'رئيس وفد' },
                        { value: 'department_head', label_en: 'Department Head', label_ar: 'رئيس قسم' },
                        { value: 'other', label_en: 'Other', label_ar: 'أخرى' }
                    ]
                },

                // Delegation Members (dynamic repeater, n-1) - Passport now optional
                {
                    id: 'members',
                    type: 'repeater',
                    label_en: 'Delegation Members',
                    label_ar: 'أعضاء الوفد',
                    trigger_field: 'delegation_count',
                    offset: -1, // n-1 (exclude head)
                    max: 20,
                    width: '100',
                    fields: [
                        { id: 'name', type: 'text', label_en: 'Full Name', label_ar: 'الاسم الثلاثي', width: '50' },
                        { id: 'title', type: 'text', label_en: 'Official Title', label_ar: 'الصفة الرسمية', width: '50' },
                        { id: 'passport', type: 'file', label_en: 'Passport Photo (optional)', label_ar: 'صورة جواز السفر (اختياري)', width: '100', required: false }
                    ]
                },

                // Head of Delegation Passport - Now optional
                { id: 'head_passport', type: 'file', label_en: 'Delegation Head - Passport Photo (optional)', label_ar: 'رئيس الوفد - صورة جواز السفر (اختياري)', required: false, width: '100' },

                // Section: Requested Services
                {
                    id: 'services',
                    type: 'checkbox',
                    label_en: 'Requested Services',
                    label_ar: 'الخدمات المطلوبة',
                    required: false,
                    width: '100',
                    options: [
                        { value: 'procedures', label_en: 'Facilitate arrival/departure procedures', label_ar: 'تسهيل إجراءات الوصول/المغادرة' },
                        { value: 'vip_lounge', label_en: 'Open VIP Lounge', label_ar: 'فتح قاعة الشرف' }
                    ]
                },

                // Section: Vehicles (dynamic repeater)
                { id: 'vehicle_count', type: 'number', label_en: 'Number of Vehicles', label_ar: 'عدد المركبات', required: false, width: '100' },
                {
                    id: 'vehicles',
                    type: 'repeater',
                    label_en: 'Vehicle Details',
                    label_ar: 'بيانات المركبات',
                    trigger_field: 'vehicle_count',
                    offset: 0,
                    max: 10,
                    width: '100',
                    fields: [
                        { id: 'type', type: 'text', label_en: 'Type', label_ar: 'النوع', width: '25' },
                        { id: 'color', type: 'text', label_en: 'Color', label_ar: 'اللون', width: '25' },
                        { id: 'plate', type: 'text', label_en: 'Plate Number', label_ar: 'رقم اللوحة', width: '25' },
                        { id: 'driver', type: 'text', label_en: 'Driver Name', label_ar: 'اسم السائق', width: '25' }
                    ]
                },

                // Section: Coordinator Information
                { id: 'coordinator_name', type: 'text', label_en: 'Coordinator - Full Name', label_ar: 'منسق الوفد - الاسم الثلاثي', required: true, width: '33' },
                { id: 'coordinator_phone', type: 'phone', label_en: 'Coordinator - Phone Number', label_ar: 'منسق الوفد - رقم الهاتف', required: true, width: '33' },
                { id: 'coordinator_email', type: 'email', label_en: 'Coordinator - Email (optional)', label_ar: 'منسق الوفد - البريد الإلكتروني (اختياري)', required: false, width: '33' },

                // Section: Notes
                { id: 'notes', type: 'textarea', label_en: 'Additional Notes', label_ar: 'ملاحظات', required: false, width: '100' },

                // Preparation Date
                { id: 'preparation_date', type: 'date', label_en: 'Preparation Date', label_ar: 'تاريخ التحرير', required: true, width: '50' },

                // Captcha
                { id: 'captcha', type: 'captcha', label_en: 'Verification', label_ar: 'تحقق', required: true, width: '100' }
            ]
        }
    }
]

const Forms = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const queryClient = useQueryClient()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingForm, setEditingForm] = useState(null)
    const [activeTab, setActiveTab] = useState(0)
    const [embedDialogOpen, setEmbedDialogOpen] = useState(false)
    const [embedFormId, setEmbedFormId] = useState(null)
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false)

    // Form users state
    const [formUsers, setFormUsers] = useState([])
    const [userDialogOpen, setUserDialogOpen] = useState(false)
    const [editingUser, setEditingUser] = useState(null)
    const [userData, setUserData] = useState({
        name_en: '',
        name_ar: '',
        email: '',
        password: '',
        is_active: true
    })
    const [confirmDeleteUser, setConfirmDeleteUser] = useState({ open: false, userId: null, name: '' })

    // Category management state
    const [formCategories, setFormCategories] = useState([])
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
    const [editingCategory, setEditingCategory] = useState(null)
    const [categoryData, setCategoryData] = useState({
        name_en: '',
        name_ar: '',
        color: '#1a3a6b',
        sort_order: 0
    })
    const [categoryLoading, setCategoryLoading] = useState(false)
    const [deleteCategoryOpen, setDeleteCategoryOpen] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState(null)

    // Fetch categories when Categories tab is opened
    const fetchFormCategories = (formId) => {
        setCategoryLoading(true)
        axios.get(`/categories?form_id=${formId}`)
            .then(res => setFormCategories(res.data || []))
            .catch(err => console.error('Failed to fetch categories:', err))
            .finally(() => setCategoryLoading(false))
    }

    useEffect(() => {
        if (activeTab === 7 && editingForm) {
            fetchFormCategories(editingForm.id)
        }
    }, [activeTab, editingForm?.id])

    const handleOpenCategoryDialog = (cat = null) => {
        if (cat) {
            setEditingCategory(cat)
            setCategoryData({
                name_en: cat.name_en || '',
                name_ar: cat.name_ar || '',
                color: cat.color || '#1a3a6b',
                sort_order: cat.sort_order || 0
            })
        } else {
            setEditingCategory(null)
            setCategoryData({ name_en: '', name_ar: '', color: '#1a3a6b', sort_order: formCategories.length })
        }
        setCategoryDialogOpen(true)
    }

    const handleSaveCategory = async () => {
        if (!categoryData.name_en || !categoryData.name_ar) {
            toast.error(language === 'ar' ? 'الاسم بالعربي والانجليزي مطلوب' : 'English and Arabic names are required')
            return
        }
        setCategoryLoading(true)
        try {
            if (editingCategory) {
                await axios.put(`/categories/${editingCategory.id}`, categoryData)
                toast.success(language === 'ar' ? 'تم تحديث التصنيف' : 'Category updated')
            } else {
                await axios.post('/categories', { ...categoryData, form_id: editingForm.id })
                toast.success(language === 'ar' ? 'تم إنشاء التصنيف' : 'Category created')
            }
            setCategoryDialogOpen(false)
            fetchFormCategories(editingForm.id)
        } catch (err) {
            toast.error(language === 'ar' ? 'فشل في حفظ التصنيف' : 'Failed to save category')
        } finally {
            setCategoryLoading(false)
        }
    }

    const handleToggleCategory = async (e, cat) => {
        e.stopPropagation()
        try {
            await axios.put(`/categories/${cat.id}`, { is_active: !cat.is_active })
            fetchFormCategories(editingForm.id)
        } catch (err) {
            toast.error('Failed to toggle category')
        }
    }

    const handleRequestDeleteCategory = (cat) => {
        setCategoryToDelete(cat)
        setDeleteCategoryOpen(true)
    }

    const handleConfirmDeleteCategory = async () => {
        if (!categoryToDelete) return
        try {
            await axios.delete(`/categories/${categoryToDelete.id}`)
            toast.success(language === 'ar' ? 'تم حذف التصنيف' : 'Category deleted')
            fetchFormCategories(editingForm.id)
            setDeleteCategoryOpen(false)
            setCategoryToDelete(null)
        } catch (err) {
            toast.error('Failed to delete category')
        }
    }

    const PRESET_COLORS = [
        '#1a3a6b', '#1976d2', '#e53935', '#f57c00', '#7b1fa2',
        '#00897b', '#5c6bc0', '#ef5350', '#ffa726', '#ab47bc',
        '#43a047', '#1e88e5', '#e91e63', '#ff7043', '#8e24aa'
    ]

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [formToDelete, setFormToDelete] = useState(null)
    const [migrateUsersTo, setMigrateUsersTo] = useState('')
    const [deleteFormUsers, setDeleteFormUsers] = useState([])
    const [isLoadingDeleteUsers, setIsLoadingDeleteUsers] = useState(false)

    // Copy users dialog state
    const [copyUsersDialogOpen, setCopyUsersDialogOpen] = useState(false)
    const [copyFromFormId, setCopyFromFormId] = useState('')
    const [isCopyingUsers, setIsCopyingUsers] = useState(false)
    // Default form data to ensure all fields have proper values
    const DEFAULT_FORM_DATA = {
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        submission_prefix: 'FORM',
        from_email: '',
        from_name: '',
        // Success & Redirect
        success_message_en: '',
        success_message_ar: '',
        redirect_url: '',
        // Header Settings
        header_enabled: true,
        header_title_en: '',
        header_title_ar: '',
        header_subtitle_en: '',
        header_subtitle_ar: '',
        // Email Header Settings
        email_header_enabled: true,
        email_header_title_en: '',
        email_header_title_ar: '',
        email_header_subtitle_en: '',
        email_header_subtitle_ar: '',
        email_header_color: '#1a3a6b',
        email_header_gradient_end: '#0d2247',
        // Style settings - MOJ Design System
        primary_color: '#1a3a6b',
        header_gradient_end: '#0d2247',
        background_color: '#1a3a6b',
        button_color: '#1a3a6b',
        button_hover_color: '#246b57',
        button_text_color: '#ffffff',
        button_text_hover_color: '#ffffff',
        form_width: '800',
        // Label styling
        label_color: '#333333',
        label_font_size: '14',
        label_font_weight: '600',
        // Input styling
        input_border_color: '#cbe5e0',
        input_focus_color: '#1a3a6b',
        // Container styling
        border_radius: '8',
        box_shadow: true,
        // New style settings
        font_family: 'Inter, Cairo, sans-serif',
        header_logo_url: '',
        email_logo_url: '',
        logo_position: 'center',
        logo_max_width: '150',
        // Button settings
        submit_text_en: 'Submit',
        submit_text_ar: 'إرسال',
        submitting_text_en: 'Submitting...',
        submitting_text_ar: 'جاري الإرسال...',
        button_width: '100',
        button_position: 'center',
        // Email Templates
        email_acknowledgment_en: '',
        email_acknowledgment_ar: '',
        email_followup_en: '',
        email_followup_ar: '',
        email_resolution_en: '',
        email_resolution_ar: '',
        // Login credentials for shared access
        login_email: '',
        login_password: '',
        // Print/PDF Settings
        print_logo_url: '',
        print_org_name_en: 'Ministry of Justice',
        print_org_name_ar: 'وزارة العدل',
        print_warning_en: '',
        print_warning_ar: '',
        print_note_en: '',
        print_note_ar: '',
        print_footer_en: '',
        print_footer_ar: '',
        // PDF Export Field Selection
        export_all_fields: true,
        export_fields: [],
        export_include_response: false,
        // Direct Submissions Settings
        enable_direct_submissions: false,
        direct_submission_name_field: '',
        // Live Chat
        enable_live_chat: false,
        fields: [],
    }
    const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA })

    const [fieldDialogOpen, setFieldDialogOpen] = useState(false)
    const [editingField, setEditingField] = useState(null)
    const [fieldData, setFieldData] = useState({
        id: '',
        type: 'text',
        label_en: '',
        label_ar: '',
        required: false,
        placeholder_en: '',
        placeholder_ar: '',
        width: '100',
        min_length: '',
        max_length: '',
        options: [],
    })

    // Fetch forms
    const { data: formsResponse, isLoading } = useQuery({
        queryKey: ['forms'],
        queryFn: () => formsApi.getAll(),
    })

    const forms = formsResponse?.data || []

    // Create form mutation
    const createMutation = useMutation({
        mutationFn: (data) => formsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['forms'])
            toast.success('Form created successfully')
            handleCloseDialog()
        },
        onError: () => {
            toast.error('Failed to create form', { toastId: 'create-form-error' })
        },
    })

    // Update form mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => formsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['forms'])
            toast.success('Form updated successfully')
            handleCloseDialog()
        },
        onError: () => {
            toast.error('Failed to update form', { toastId: 'update-form-error' })
        },
    })

    // Delete form mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => formsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['forms'])
            toast.success('Form deleted successfully')
        },
        onError: () => {
            toast.error('Failed to delete form')
        },
    })

    // Fetch form users when editing a form
    const fetchFormUsers = async (formId) => {
        try {
            const response = await formUsersApi.getByForm(formId)
            setFormUsers(response.data || [])
        } catch (error) {
            console.error('Failed to fetch form users:', error)
            setFormUsers([])
        }
    }

    // Create form user mutation
    const createUserMutation = useMutation({
        mutationFn: (data) => formUsersApi.create(data),
        onSuccess: () => {
            toast.success('User created successfully')
            if (editingForm) fetchFormUsers(editingForm.id)
            handleCloseUserDialog()
        },
        onError: () => {
            toast.error('Failed to create user')
        },
    })

    // Update form user mutation
    const updateUserMutation = useMutation({
        mutationFn: ({ id, data }) => formUsersApi.update(id, data),
        onSuccess: () => {
            toast.success('User updated successfully')
            if (editingForm) fetchFormUsers(editingForm.id)
            handleCloseUserDialog()
        },
        onError: () => {
            toast.error('Failed to update user')
        },
    })

    // Delete form user mutation
    const deleteUserMutation = useMutation({
        mutationFn: (id) => formUsersApi.delete(id),
        onSuccess: () => {
            toast.success('User deleted successfully')
            if (editingForm) fetchFormUsers(editingForm.id)
        },
        onError: () => {
            toast.error('Failed to delete user')
        },
    })

    const handleOpenUserDialog = (user = null) => {
        if (user) {
            setEditingUser(user)
            setUserData({
                name_en: user.name_en || '',
                name_ar: user.name_ar || '',
                password: '', // Don't populate password for security
                is_active: user.is_active !== false
            })
        } else {
            setEditingUser(null)
            setUserData({
                name_en: '',
                name_ar: '',
                password: '',
                is_active: true
            })
        }
        setUserDialogOpen(true)
    }

    const handleCloseUserDialog = () => {
        setUserDialogOpen(false)
        setEditingUser(null)
        setUserData({
            name_en: '',
            name_ar: '',
            email: '',
            password: '',
            is_active: true
        })
    }

    const handleSaveUser = () => {
        if (!userData.name_en) {
            toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name is required')
            return
        }
        if (!editingUser && !userData.password) {
            toast.error(language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required for new users')
            return
        }

        const payload = {
            form_id: editingForm.id,
            name_en: userData.name_en,
            name_ar: userData.name_ar,
            is_active: userData.is_active
        }
        if (userData.email) {
            payload.email = userData.email
        }
        if (userData.password) {
            payload.password = userData.password
        }

        if (editingUser) {
            updateUserMutation.mutate({ id: editingUser.id, data: payload })
        } else {
            createUserMutation.mutate(payload)
        }
    }

    const handleDeleteUser = (userId) => {
        // Find the user name for the confirmation message
        const userToDelete = formUsers.find(u => u.id === userId)
        const name = userToDelete?.name_en || userToDelete?.name_ar || userToDelete?.email || ''
        setConfirmDeleteUser({ open: true, userId, name })
    }

    const handleConfirmDeleteUser = () => {
        deleteUserMutation.mutate(confirmDeleteUser.userId)
        setConfirmDeleteUser({ open: false, userId: null, name: '' })
    }

    const handleOpenDialog = (form = null) => {
        if (form) {
            setEditingForm(form)
            const s = form.settings || {} // Settings object shorthand
            setFormData({
                title: form.title || '',
                title_ar: form.title_ar || '',
                description: form.description || '',
                description_ar: form.description_ar || '',
                submission_prefix: form.submission_prefix || 'FORM',
                login_email: form.login_email || '',
                from_email: s.from_email || '',
                from_name: s.from_name || '',
                // Success & Redirect
                success_message_en: s.success_message_en || '',
                success_message_ar: s.success_message_ar || '',
                redirect_url: s.redirect_url || '',
                // Access Settings
                require_login: s.require_login || false,
                generate_qr_code: form.generate_qr_code || false,
                // Header Settings
                header_enabled: s.header_enabled !== false,
                header_title_en: s.header_title_en || '',
                header_title_ar: s.header_title_ar || '',
                header_subtitle_en: s.header_subtitle_en || '',
                header_subtitle_ar: s.header_subtitle_ar || '',
                // Email Header Settings
                email_header_enabled: s.email_header_enabled !== false,
                email_header_title_en: s.email_header_title_en || '',
                email_header_title_ar: s.email_header_title_ar || '',
                email_header_subtitle_en: s.email_header_subtitle_en || '',
                email_header_subtitle_ar: s.email_header_subtitle_ar || '',
                email_header_color: s.email_header_color || '#1a3a6b',
                email_header_gradient_end: s.email_header_gradient_end || '#0d2247',
                // Style settings - color defaults
                primary_color: s.primary_color || form.primary_color || '#1a3a6b',
                header_gradient_end: s.header_gradient_end || form.header_gradient_end || '#0d2247',
                background_color: s.background_color || form.background_color || '#1a3a6b',
                button_color: s.button_color || form.button_color || '#1a3a6b',
                button_hover_color: s.button_hover_color || '#246b57',
                button_text_color: s.button_text_color || '#ffffff',
                button_text_hover_color: s.button_text_hover_color || '#ffffff',
                form_width: s.form_width || form.form_width || '800',
                // Label styling
                label_color: s.label_color || '#333333',
                label_font_size: s.label_font_size || '14',
                label_font_weight: s.label_font_weight || '600',
                // Input styling
                input_border_color: s.input_border_color || '#cbe5e0',
                input_focus_color: s.input_focus_color || '#1a3a6b',
                // Container styling
                border_radius: s.border_radius || '8',
                box_shadow: s.box_shadow !== false,
                // Font and logos
                font_family: s.font_family || form.font_family || 'Inter, Cairo, sans-serif',
                header_logo_url: s.header_logo_url || form.header_logo_url || '',
                email_logo_url: s.email_logo_url || form.email_logo_url || '',
                logo_position: s.logo_position || 'center',
                logo_max_width: s.logo_max_width || '150',
                // Button settings
                submit_text_en: s.submit_text_en || 'Submit',
                submit_text_ar: s.submit_text_ar || 'إرسال',
                submitting_text_en: s.submitting_text_en || 'Submitting...',
                submitting_text_ar: s.submitting_text_ar || 'جاري الإرسال...',
                button_width: s.button_width || '100',
                button_position: s.button_position || 'center',
                // Email Templates
                email_acknowledgment_en: s.email_acknowledgment_en || '',
                email_acknowledgment_ar: s.email_acknowledgment_ar || '',
                email_followup_en: s.email_followup_en || '',
                email_followup_ar: s.email_followup_ar || '',
                email_resolution_en: s.email_resolution_en || '',
                email_resolution_ar: s.email_resolution_ar || '',
                // Print/PDF Settings
                print_logo_url: s.print_logo_url || '',
                print_org_name_en: s.print_org_name_en || 'Ministry of Justice',
                print_org_name_ar: s.print_org_name_ar || 'وزارة العدل',
                print_warning_en: s.print_warning_en || '',
                print_warning_ar: s.print_warning_ar || '',
                print_note_en: s.print_note_en || '',
                print_note_ar: s.print_note_ar || '',
                print_footer_en: s.print_footer_en || '',
                print_footer_ar: s.print_footer_ar || '',
                // PDF Export Field Selection
                export_all_fields: s.export_all_fields !== false,
                export_fields: s.export_fields || [],
                export_columns: s.export_columns || 1,
                export_field_columns: s.export_field_columns || {},
                export_include_response: s.export_include_response || false,
                // Direct Submissions Settings
                enable_direct_submissions: form.enable_direct_submissions || false,
                direct_submission_name_field: form.direct_submission_name_field || '',
                // Live Chat
                enable_live_chat: form.enable_live_chat || false,
                settings: form.settings || {},
                fields: (() => {
                    try {
                        let fields = form.fields;
                        if (typeof fields === 'string') {
                            fields = JSON.parse(fields);
                        }
                        if (typeof fields === 'string') {
                            fields = JSON.parse(fields);
                        }
                        return Array.isArray(fields) ? fields : [];
                    } catch { return []; }
                })(),
            })
            // Fetch form users for editing
            fetchFormUsers(form.id)
        } else {
            setEditingForm(null)
            setFormData({ ...DEFAULT_FORM_DATA })
            setFormUsers([])
        }
        setActiveTab(0)
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setEditingForm(null)
        setFormData({ ...DEFAULT_FORM_DATA })
    }

    // Handle using a form template
    const handleUseTemplate = (template) => {
        setFormData({
            ...DEFAULT_FORM_DATA,
            ...template.data,
            from_email: '',
            from_name: '',
        })
        setTemplateDialogOpen(false)
        setDialogOpen(true)
        setActiveTab(0)
    }

    const handleSaveForm = () => {
        if (!formData.title.trim()) {
            toast.error('Form title is required')
            return
        }

        // Bundle style settings into settings object for backend
        const settings = {
            // Access Settings
            require_login: formData.require_login || false,
            // Success & Redirect
            success_message_en: formData.success_message_en,
            success_message_ar: formData.success_message_ar,
            redirect_url: formData.redirect_url,
            // Header Settings
            header_enabled: formData.header_enabled,
            header_title_en: formData.header_title_en,
            header_title_ar: formData.header_title_ar,
            header_subtitle_en: formData.header_subtitle_en,
            header_subtitle_ar: formData.header_subtitle_ar,
            // Email Header Settings
            email_header_enabled: formData.email_header_enabled,
            email_header_title_en: formData.email_header_title_en,
            email_header_title_ar: formData.email_header_title_ar,
            email_header_subtitle_en: formData.email_header_subtitle_en,
            email_header_subtitle_ar: formData.email_header_subtitle_ar,
            email_header_color: formData.email_header_color,
            email_header_gradient_end: formData.email_header_gradient_end,
            // Colors
            primary_color: formData.primary_color,
            header_gradient_end: formData.header_gradient_end,
            background_color: formData.background_color,
            button_color: formData.button_color,
            button_hover_color: formData.button_hover_color,
            button_text_color: formData.button_text_color,
            button_text_hover_color: formData.button_text_hover_color,
            form_width: formData.form_width,
            // Label styling
            label_color: formData.label_color,
            label_font_size: formData.label_font_size,
            label_font_weight: formData.label_font_weight,
            // Input styling
            input_border_color: formData.input_border_color,
            input_focus_color: formData.input_focus_color,
            // Container styling
            border_radius: formData.border_radius,
            box_shadow: formData.box_shadow,
            // Font and logos
            font_family: formData.font_family,
            header_logo_url: formData.header_logo_url,
            email_logo_url: formData.email_logo_url,
            logo_position: formData.logo_position,
            logo_max_width: formData.logo_max_width,
            // Button settings
            submit_text_en: formData.submit_text_en,
            submit_text_ar: formData.submit_text_ar,
            submitting_text_en: formData.submitting_text_en,
            submitting_text_ar: formData.submitting_text_ar,
            button_width: formData.button_width,
            button_position: formData.button_position,
            // Email Templates
            email_acknowledgment_en: formData.email_acknowledgment_en,
            email_acknowledgment_ar: formData.email_acknowledgment_ar,
            email_followup_en: formData.email_followup_en,
            email_followup_ar: formData.email_followup_ar,
            email_resolution_en: formData.email_resolution_en,
            email_resolution_ar: formData.email_resolution_ar,
            // Form Email Settings (sender override) - v2026.01.12
            from_email: formData.from_email,
            from_name: formData.from_name,
            // Print/PDF Settings
            print_logo_url: formData.print_logo_url,
            print_org_name_en: formData.print_org_name_en,
            print_org_name_ar: formData.print_org_name_ar,
            print_warning_en: formData.print_warning_en,
            print_warning_ar: formData.print_warning_ar,
            print_note_en: formData.print_note_en,
            print_note_ar: formData.print_note_ar,
            print_footer_en: formData.print_footer_en,
            print_footer_ar: formData.print_footer_ar,
            // PDF Export Field Selection
            export_all_fields: formData.export_all_fields,
            export_fields: formData.export_fields,
            export_columns: formData.export_columns || 1,
            export_field_columns: formData.export_field_columns || {},
            export_include_response: formData.export_include_response || false,
            // Live Chat Settings
            chat_primary_color: formData.settings?.chat_primary_color || '#1a3a6b',
            chat_secondary_color: formData.settings?.chat_secondary_color || '#0d2247',
            chat_header_title_en: formData.settings?.chat_header_title_en || '',
            chat_header_title_ar: formData.settings?.chat_header_title_ar || '',
            chat_header_subtitle_en: formData.settings?.chat_header_subtitle_en || '',
            chat_header_subtitle_ar: formData.settings?.chat_header_subtitle_ar || '',
            chat_logo_url: formData.settings?.chat_logo_url || '',
            chat_welcome_message: formData.settings?.chat_welcome_message || '',
            chat_welcome_message_ar: formData.settings?.chat_welcome_message_ar || '',
            chat_offline_message: formData.settings?.chat_offline_message || '',
            chat_offline_message_ar: formData.settings?.chat_offline_message_ar || '',
            chat_widget_color: formData.settings?.chat_widget_color || '#1976d2',
            chat_require_email: formData.settings?.chat_require_email || false,
        }

        const payload = {
            title: formData.title,
            title_ar: formData.title_ar,
            description: formData.description,
            description_ar: formData.description_ar,
            submission_prefix: formData.submission_prefix,
            login_email: formData.login_email,
            generate_qr_code: formData.generate_qr_code ? 1 : 0,
            enable_direct_submissions: formData.enable_direct_submissions ? 1 : 0,
            direct_submission_name_field: formData.direct_submission_name_field || null,
            enable_live_chat: formData.enable_live_chat ? 1 : 0,
            fields: formData.fields,
            settings: settings,
        }

        if (editingForm) {
            updateMutation.mutate({ id: editingForm.id, data: payload })
        } else {
            createMutation.mutate(payload)
        }
    }

    const handleDeleteForm = async (id) => {
        // Find the form to delete
        const form = forms.find(f => f.id === id)
        if (!form) return

        setFormToDelete(form)
        setMigrateUsersTo('')
        setDeleteDialogOpen(true)
        setIsLoadingDeleteUsers(true)

        // Fetch users for this form
        try {
            const response = await formUsersApi.getByForm(id)
            setDeleteFormUsers(response.data || [])
        } catch (error) {
            console.error('Failed to fetch form users:', error)
            setDeleteFormUsers([])
        } finally {
            setIsLoadingDeleteUsers(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!formToDelete) return

        // If migration target is selected and there are users, migrate them first
        if (migrateUsersTo && deleteFormUsers.length > 0) {
            try {
                // Migrate users to target form
                await formUsersApi.migrateUsers(formToDelete.id, migrateUsersTo)
                toast.success(language === 'ar'
                    ? `تم نقل ${deleteFormUsers.length} مستخدمين بنجاح`
                    : `Successfully migrated ${deleteFormUsers.length} users`)
            } catch (error) {
                toast.error(language === 'ar'
                    ? 'فشل في نقل المستخدمين'
                    : 'Failed to migrate users')
                return // Don't delete if migration fails
            }
        }

        // Now delete the form
        deleteMutation.mutate(formToDelete.id)

        // Close dialog and reset
        setDeleteDialogOpen(false)
        setFormToDelete(null)
        setMigrateUsersTo('')
        setDeleteFormUsers([])
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setFormToDelete(null)
        setMigrateUsersTo('')
        setDeleteFormUsers([])
    }

    // Copy users from another form
    const handleCopyUsers = async () => {
        if (!copyFromFormId || !editingForm?.id) return

        setIsCopyingUsers(true)
        try {
            const result = await formUsersApi.copyUsers(copyFromFormId, editingForm.id)
            toast.success(language === 'ar'
                ? `تم نسخ ${result.data?.copied_count || 0} مستخدمين بنجاح`
                : `Successfully copied ${result.data?.copied_count || 0} users`)

            // Refresh form users list
            const response = await formUsersApi.getByForm(editingForm.id)
            setFormUsers(response.data || [])

            setCopyUsersDialogOpen(false)
            setCopyFromFormId('')
        } catch (error) {
            toast.error(language === 'ar'
                ? 'فشل في نسخ المستخدمين'
                : 'Failed to copy users')
        } finally {
            setIsCopyingUsers(false)
        }
    }

    // Field management
    const handleOpenFieldDialog = (field = null, index = null) => {
        if (field) {
            setEditingField({ ...field, index })
            setFieldData({
                id: field.id || '',
                type: field.type || 'text',
                label_en: field.label_en || '',
                label_ar: field.label_ar || '',
                required: field.required || false,
                require_verification: field.require_verification || false,
                placeholder_en: field.placeholder_en || '',
                placeholder_ar: field.placeholder_ar || '',
                width: field.width || '100',
                min_length: field.min_length || '',
                max_length: field.max_length || '',
                options: field.options || [],
                // Repeater-specific properties
                fields: field.fields || [],
                trigger_field: field.trigger_field || '',
                offset: field.offset ?? 0,
                max: field.max ?? 10,
                // Allow Other option properties
                allowOther: field.allowOther || false,
                otherLabel_en: field.otherLabel_en || 'Please specify',
                otherLabel_ar: field.otherLabel_ar || 'يرجى التحديد',
                // Conditional visibility properties
                conditionalVisibility: field.conditionalVisibility || null,
            })
        } else {
            setEditingField(null)
            setFieldData({
                id: '',
                type: 'text',
                label_en: '',
                label_ar: '',
                required: false,
                placeholder_en: '',
                placeholder_ar: '',
                width: '100',
                min_length: '',
                max_length: '',
                options: [],
            })
        }
        setFieldDialogOpen(true)
    }

    const handleCloseFieldDialog = () => {
        setFieldDialogOpen(false)
        setEditingField(null)
    }

    const handleSaveField = () => {
        if (!fieldData.label_en.trim()) {
            toast.error('Field label is required')
            return
        }

        const newField = {
            ...fieldData,
            id: fieldData.id || fieldData.label_en.toLowerCase().replace(/\s+/g, '_'),
        }

        if (editingField && editingField.index !== null) {
            const updatedFields = [...formData.fields]
            updatedFields[editingField.index] = newField
            setFormData({ ...formData, fields: updatedFields })
        } else {
            setFormData({ ...formData, fields: [...formData.fields, newField] })
        }

        handleCloseFieldDialog()
    }

    const handleRemoveField = (index) => {
        const updatedFields = formData.fields.filter((_, i) => i !== index)
        setFormData({ ...formData, fields: updatedFields })
    }

    const handleMoveField = (index, direction) => {
        const newIndex = index + direction
        if (newIndex < 0 || newIndex >= formData.fields.length) return

        const updatedFields = [...formData.fields]
        const temp = updatedFields[index]
        updatedFields[index] = updatedFields[newIndex]
        updatedFields[newIndex] = temp
        setFormData({ ...formData, fields: updatedFields })
    }

    const copyFormLink = (formId) => {
        const link = `${window.location.origin}/form/${formId}`
        navigator.clipboard.writeText(link)
        toast.success('Form link copied to clipboard')
    }

    const handleOpenEmbedDialog = (formId) => {
        setEmbedFormId(formId)
        setEmbedDialogOpen(true)
    }

    const getEmbedCode = (lang = 'en') => {
        const baseUrl = window.location.origin
        return `<!-- Form Management System Embed (${lang === 'ar' ? 'Arabic' : 'English'}) -->
<iframe 
  src="${baseUrl}/form/${embedFormId}?embed=true&lang=${lang}" 
  width="100%" 
  height="1200" 
  frameborder="0"
  scrolling="no"
  style="border: none; border-radius: 12px; overflow: hidden;">
</iframe>`
    }

    const copyEmbedCode = (lang = 'en') => {
        navigator.clipboard.writeText(getEmbedCode(lang))
        toast.success(`${lang === 'ar' ? 'Arabic' : 'English'} embed code copied!`)
    }

    if (isLoading) {
        return <Loading message={t('common.loading')} />
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h4" fontWeight={700}>
                    {t('nav.forms')}
                </Typography>
                {user?.user_type === 'admin' && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setTemplateDialogOpen(true)}
                        >
                            📋 {t('forms.useTemplate')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                        >
                            {t('forms.createForm')}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Forms Grid */}
            {forms.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">
                        No forms found. Create your first form!
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {forms.map((form) => (
                        <Grid item xs={12} md={6} lg={4} key={form.id}>
                            <Card sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        {language === 'ar' ? form.title_ar || form.title : form.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {language === 'ar' ? form.description_ar || form.description : form.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`${(() => {
                                                try {
                                                    let fields = form.fields;
                                                    // Parse if string
                                                    if (typeof fields === 'string') {
                                                        fields = JSON.parse(fields);
                                                    }
                                                    // If still a string (double-encoded), parse again
                                                    if (typeof fields === 'string') {
                                                        fields = JSON.parse(fields);
                                                    }
                                                    return Array.isArray(fields) ? fields.length : 0;
                                                } catch { return 0; }
                                            })()} fields`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={form.submission_prefix}
                                            size="small"
                                        />
                                        <Chip
                                            label={form.is_active ? 'Active' : 'Inactive'}
                                            size="small"
                                            color={form.is_active ? 'success' : 'default'}
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <IconButton size="small" onClick={() => handleOpenDialog(form)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => copyFormLink(form.id)}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => window.open(`/form/${form.id}`, '_blank')}
                                    >
                                        <Visibility fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenEmbedDialog(form.id)}
                                        title="Get Embed Code"
                                    >
                                        <Code fontSize="small" />
                                    </IconButton>
                                    {user?.user_type === 'admin' && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteForm(form.id)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Form Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingForm ? t('forms.editForm') : t('forms.createForm')}
                </DialogTitle>
                <DialogContent>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                        <Tab label={t('forms.basicInfo')} />
                        <Tab label={t('forms.fields')} />
                        {editingForm && <Tab label={language === 'ar' ? 'المستخدمون' : 'Users'} icon={<People fontSize="small" />} iconPosition="start" />}
                        {editingForm && <Tab label="WhatsApp" icon={<WhatsApp fontSize="small" />} iconPosition="start" />}
                        {editingForm && <Tab label={language === 'ar' ? 'إعدادات' : 'Settings'} icon={<Settings fontSize="small" />} iconPosition="start" />}
                        {editingForm && <Tab label={language === 'ar' ? 'المحادثة المباشرة' : 'Live Chat'} icon={<Forum fontSize="small" />} iconPosition="start" />}
                        {editingForm && <Tab label={language === 'ar' ? 'API خارجي' : 'External API'} icon={<Api fontSize="small" />} iconPosition="start" />}
                        {editingForm && <Tab label={language === 'ar' ? 'التصنيفات' : 'Categories'} icon={<Label fontSize="small" />} iconPosition="start" />}
                    </Tabs>

                    {activeTab === 0 && (
                        <Box sx={{ pt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.formTitleEn')}
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.formTitleAr')}
                                        value={formData.title_ar}
                                        onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                                        dir="rtl"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.descriptionEn')}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        multiline
                                        rows={3}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.descriptionAr')}
                                        value={formData.description_ar}
                                        onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                                        multiline
                                        rows={3}
                                        dir="rtl"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.submissionIdPrefix')}
                                        value={formData.submission_prefix}
                                        onChange={(e) => setFormData({ ...formData, submission_prefix: e.target.value.toUpperCase() })}
                                        helperText={t('forms.submissionIdPrefixHelper') || "e.g., CONTACT, SUPPORT, INQUIRY"}
                                    />
                                </Grid>
                            </Grid>

                            {/* Email Settings Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.emailSettings')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.fromEmail')}
                                        type="email"
                                        value={formData.from_email}
                                        onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                                        placeholder="noreply@yourdomain.com"
                                        helperText={t('forms.senderEmailHelper')}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.fromName')}
                                        value={formData.from_name}
                                        onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                                        placeholder="Form Management System"
                                        helperText={t('forms.senderNameHelper')}
                                    />
                                </Grid>
                            </Grid>

                            {/* Success & Redirect Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.successRedirectSettings')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.successMessageEn')}
                                        value={formData.success_message_en}
                                        onChange={(e) => setFormData({ ...formData, success_message_en: e.target.value })}
                                        multiline
                                        rows={2}
                                        placeholder="Thank you for your submission!"
                                        helperText={t('forms.shownAfterSubmission') || "Shown after form submission"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.successMessageAr')}
                                        value={formData.success_message_ar}
                                        onChange={(e) => setFormData({ ...formData, success_message_ar: e.target.value })}
                                        multiline
                                        rows={2}
                                        dir="rtl"
                                        placeholder="شكراً لتقديم طلبك!"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('forms.redirectUrl')}
                                        value={formData.redirect_url}
                                        onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                                        placeholder="https://example.com/thank-you"
                                        helperText={t('forms.redirectUrlHelper')}
                                    />
                                </Grid>
                            </Grid>

                            {/* Access Settings Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {language === 'ar' ? 'إعدادات الوصول' : 'Access Settings'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formData.require_login}
                                                onChange={(e) => setFormData({ ...formData, require_login: e.target.checked })}
                                            />
                                        }
                                        label={language === 'ar' ? 'يتطلب تسجيل الدخول للإرسال' : 'Require Login to Submit'}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                        {language === 'ar'
                                            ? 'عند التفعيل، سيحتاج المستخدمون إلى إنشاء حساب وتسجيل الدخول قبل إرسال النموذج'
                                            : 'When enabled, users will need to create an account and login before submitting the form'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formData.generate_qr_code}
                                                onChange={(e) => setFormData({ ...formData, generate_qr_code: e.target.checked })}
                                            />
                                        }
                                        label={language === 'ar' ? 'إنشاء رمز QR عند الموافقة' : 'Generate QR Code on Approval'}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                        {language === 'ar'
                                            ? 'عند التفعيل، سيتم إنشاء رمز QR ورمز تحقق للطلبات المعتمدة يمكن مسحه للتحقق'
                                            : 'When enabled, approved submissions will get a verification code and QR code that can be scanned to verify'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formData.enable_direct_submissions}
                                                onChange={(e) => setFormData({ ...formData, enable_direct_submissions: e.target.checked })}
                                            />
                                        }
                                        label={language === 'ar' ? 'تفعيل الإدخال المباشر' : 'Enable Direct Submissions'}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                        {language === 'ar'
                                            ? 'يسمح لمستخدمي لوحة التحكم بإنشاء طلبات مباشرة نيابة عن المراجعين'
                                            : 'Allows dashboard users to create submissions directly on behalf of applicants'}
                                    </Typography>
                                </Grid>
                                {formData.enable_direct_submissions && formData.fields?.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label={language === 'ar' ? 'حقل البريد الإلكتروني للمنشئ' : 'Creator Email Field'}
                                            value={formData.direct_submission_name_field || ''}
                                            onChange={(e) => setFormData({ ...formData, direct_submission_name_field: e.target.value })}
                                            helperText={language === 'ar'
                                                ? 'الحقل الذي سيتم تعبئته تلقائياً ببريد المستخدم الذي أنشأ الطلب'
                                                : 'The field that will be auto-filled with the creator\'s email'}
                                            SelectProps={{ native: true }}
                                        >
                                            <option value="">{language === 'ar' ? '(لا شيء)' : '(None)'}</option>
                                            {formData.fields
                                                .filter(f => ['text', 'name', 'email'].includes(f.type))
                                                .map(f => (
                                                    <option key={f.id} value={f.id}>
                                                        {language === 'ar' ? f.label_ar || f.label_en : f.label_en || f.label_ar}
                                                    </option>
                                                ))
                                            }
                                        </TextField>
                                    </Grid>
                                )}
                            </Grid>


                            {/* Header Settings Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.formHeaderSettings')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formData.header_enabled}
                                                onChange={(e) => setFormData({ ...formData, header_enabled: e.target.checked })}
                                            />
                                        }
                                        label={t('forms.enableFormHeader')}
                                    />
                                </Grid>
                                {formData.header_enabled && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.headerTitleEn')}
                                                value={formData.header_title_en || ''}
                                                onChange={(e) => setFormData({ ...formData, header_title_en: e.target.value })}
                                                placeholder="Contact Us"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.headerTitleAr')}
                                                value={formData.header_title_ar || ''}
                                                onChange={(e) => setFormData({ ...formData, header_title_ar: e.target.value })}
                                                dir="rtl"
                                                placeholder="تواصل معنا"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.headerSubtitleEn')}
                                                value={formData.header_subtitle_en || ''}
                                                onChange={(e) => setFormData({ ...formData, header_subtitle_en: e.target.value })}
                                                placeholder="We're here to help"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.headerSubtitleAr')}
                                                value={formData.header_subtitle_ar || ''}
                                                onChange={(e) => setFormData({ ...formData, header_subtitle_ar: e.target.value })}
                                                dir="rtl"
                                                placeholder="نحن هنا للمساعدة"
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">{t('forms.headerColor1')}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <input
                                                    type="color"
                                                    value={formData.primary_color || '#1a3a6b'}
                                                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={formData.primary_color || '#1a3a6b'}
                                                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                                    sx={{ width: 100 }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">{t('forms.headerColor2')}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <input
                                                    type="color"
                                                    value={formData.header_gradient_end || '#0d2247'}
                                                    onChange={(e) => setFormData({ ...formData, header_gradient_end: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={formData.header_gradient_end || '#0d2247'}
                                                    onChange={(e) => setFormData({ ...formData, header_gradient_end: e.target.value })}
                                                    sx={{ width: 100 }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Form Header Preview</Typography>
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    background: `linear-gradient(135deg, ${formData.primary_color || '#1a3a6b'} 0%, ${formData.header_gradient_end || '#0d2247'} 100%)`,
                                                    color: '#fff',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {formData.header_title_en || 'Form Header Title'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    {formData.header_subtitle_en || 'Form Header Subtitle'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Email Header Settings Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.emailHeaderSettings')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formData.email_header_enabled}
                                                onChange={(e) => setFormData({ ...formData, email_header_enabled: e.target.checked })}
                                            />
                                        }
                                        label={t('forms.enableEmailHeader')}
                                    />
                                </Grid>
                                {formData.email_header_enabled && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.emailHeaderTitleEn')}
                                                value={formData.email_header_title_en || ''}
                                                onChange={(e) => setFormData({ ...formData, email_header_title_en: e.target.value })}
                                                placeholder="Ministry of Justice"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.emailHeaderTitleAr')}
                                                value={formData.email_header_title_ar || ''}
                                                onChange={(e) => setFormData({ ...formData, email_header_title_ar: e.target.value })}
                                                dir="rtl"
                                                placeholder="وزارة العدل"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.emailHeaderSubtitleEn')}
                                                value={formData.email_header_subtitle_en || ''}
                                                onChange={(e) => setFormData({ ...formData, email_header_subtitle_en: e.target.value })}
                                                placeholder="Complaint Management System"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t('forms.emailHeaderSubtitleAr')}
                                                value={formData.email_header_subtitle_ar || ''}
                                                onChange={(e) => setFormData({ ...formData, email_header_subtitle_ar: e.target.value })}
                                                dir="rtl"
                                                placeholder="نظام إدارة الشكاوى"
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">{t('forms.emailHeaderColor1')}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <input
                                                    type="color"
                                                    value={formData.email_header_color || '#1a3a6b'}
                                                    onChange={(e) => setFormData({ ...formData, email_header_color: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={formData.email_header_color || '#1a3a6b'}
                                                    onChange={(e) => setFormData({ ...formData, email_header_color: e.target.value })}
                                                    sx={{ width: 100 }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">{t('forms.emailHeaderColor2')}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <input
                                                    type="color"
                                                    value={formData.email_header_gradient_end || '#0d2247'}
                                                    onChange={(e) => setFormData({ ...formData, email_header_gradient_end: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={formData.email_header_gradient_end || '#0d2247'}
                                                    onChange={(e) => setFormData({ ...formData, email_header_gradient_end: e.target.value })}
                                                    sx={{ width: 100 }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Email Header Preview</Typography>
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    background: `linear-gradient(135deg, ${formData.email_header_color || '#1a3a6b'} 0%, ${formData.email_header_gradient_end || '#0d2247'} 100%)`,
                                                    color: '#fff',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {formData.email_header_title_en || 'Email Header Title'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    {formData.email_header_subtitle_en || 'Email Header Subtitle'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Form Style Settings Section */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                Form Style
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Background Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input
                                            type="color"
                                            value={formData.background_color}
                                            onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                                            style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                        />
                                        <TextField
                                            size="small"
                                            value={formData.background_color}
                                            onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                                            sx={{ width: 100 }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Button Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input
                                            type="color"
                                            value={formData.button_color}
                                            onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                                            style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                        />
                                        <TextField
                                            size="small"
                                            value={formData.button_color}
                                            onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                                            sx={{ width: 100 }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.formWidth')}</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={formData.form_width}
                                        onChange={(e) => setFormData({ ...formData, form_width: e.target.value })}
                                        sx={{ mt: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.fontFamily')}</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        select
                                        SelectProps={{ native: true }}
                                        value={formData.font_family}
                                        onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
                                        sx={{ mt: 1 }}
                                    >
                                        <option value="Inter, Cairo, sans-serif">Inter / Cairo</option>
                                        <option value="Roboto, Cairo, sans-serif">Roboto / Cairo</option>
                                        <option value="Open Sans, Cairo, sans-serif">Open Sans / Cairo</option>
                                        <option value="Poppins, Cairo, sans-serif">Poppins / Cairo</option>
                                        <option value="Montserrat, Cairo, sans-serif">Montserrat / Cairo</option>
                                        <option value="Lato, Cairo, sans-serif">Lato / Cairo</option>
                                        <option value="Georgia, serif">Georgia (Serif)</option>
                                        <option value="Times New Roman, serif">Times New Roman</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.headerLogo')}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="https://example.com/logo.png"
                                            value={formData.header_logo_url}
                                            onChange={(e) => setFormData({ ...formData, header_logo_url: e.target.value })}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            component="label"
                                            sx={{ minWidth: 100 }}
                                        >
                                            {t('forms.uploadLogo') || 'Upload'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0]
                                                    if (file) {
                                                        try {
                                                            const res = await uploadApi.upload(file)
                                                            setFormData({ ...formData, header_logo_url: res.data.url })
                                                            toast.success('Logo uploaded')
                                                        } catch (err) {
                                                            toast.error('Upload failed')
                                                        }
                                                    }
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                    {formData.header_logo_url && (
                                        <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                            <img
                                                src={formData.header_logo_url}
                                                alt="Header Logo Preview"
                                                style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }}
                                                onError={(e) => { e.target.style.display = 'none' }}
                                            />
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">Email Logo</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="https://example.com/email-logo.png"
                                            value={formData.email_logo_url}
                                            onChange={(e) => setFormData({ ...formData, email_logo_url: e.target.value })}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            component="label"
                                            sx={{ minWidth: 100 }}
                                        >
                                            Upload
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0]
                                                    if (file) {
                                                        try {
                                                            const res = await uploadApi.upload(file)
                                                            setFormData({ ...formData, email_logo_url: res.data.url })
                                                            toast.success('Logo uploaded')
                                                        } catch (err) {
                                                            toast.error('Upload failed')
                                                        }
                                                    }
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                    {formData.email_logo_url && (
                                        <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                            <img
                                                src={formData.email_logo_url}
                                                alt="Email Logo Preview"
                                                style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }}
                                                onError={(e) => { e.target.style.display = 'none' }}
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Button & Submit Settings */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                Button Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Button Hover Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input
                                            type="color"
                                            value={formData.button_hover_color}
                                            onChange={(e) => setFormData({ ...formData, button_hover_color: e.target.value })}
                                            style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                        />
                                        <TextField size="small" value={formData.button_hover_color} onChange={(e) => setFormData({ ...formData, button_hover_color: e.target.value })} sx={{ width: 100 }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Button Text Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input
                                            type="color"
                                            value={formData.button_text_color}
                                            onChange={(e) => setFormData({ ...formData, button_text_color: e.target.value })}
                                            style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                        />
                                        <TextField size="small" value={formData.button_text_color} onChange={(e) => setFormData({ ...formData, button_text_color: e.target.value })} sx={{ width: 100 }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Button Width</Typography>
                                    <TextField fullWidth size="small" select SelectProps={{ native: true }} value={formData.button_width} onChange={(e) => setFormData({ ...formData, button_width: e.target.value })} sx={{ mt: 1 }}>
                                        <option value="100">100% (Full Width)</option>
                                        <option value="50">50%</option>
                                        <option value="33">33%</option>
                                        <option value="25">25%</option>
                                        <option value="auto">Auto</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Button Position</Typography>
                                    <TextField fullWidth size="small" select SelectProps={{ native: true }} value={formData.button_position} onChange={(e) => setFormData({ ...formData, button_position: e.target.value })} sx={{ mt: 1 }}>
                                        <option value="center">Center</option>
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField fullWidth size="small" label="Submit Text (EN)" value={formData.submit_text_en} onChange={(e) => setFormData({ ...formData, submit_text_en: e.target.value })} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField fullWidth size="small" label="Submit Text (AR)" value={formData.submit_text_ar} onChange={(e) => setFormData({ ...formData, submit_text_ar: e.target.value })} dir="rtl" />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField fullWidth size="small" label="Submitting Text (EN)" value={formData.submitting_text_en} onChange={(e) => setFormData({ ...formData, submitting_text_en: e.target.value })} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField fullWidth size="small" label="Submitting Text (AR)" value={formData.submitting_text_ar} onChange={(e) => setFormData({ ...formData, submitting_text_ar: e.target.value })} dir="rtl" />
                                </Grid>
                            </Grid>

                            {/* Logo Settings */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.logoSettings') || 'Logo Settings'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.logoPosition') || 'Logo Position'}</Typography>
                                    <TextField fullWidth size="small" select SelectProps={{ native: true }} value={formData.logo_position} onChange={(e) => setFormData({ ...formData, logo_position: e.target.value })} sx={{ mt: 1 }}>
                                        <option value="center">Center</option>
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.logoMaxWidth') || 'Logo Max Width (px)'}</Typography>
                                    <TextField fullWidth size="small" type="number" value={formData.logo_max_width} onChange={(e) => setFormData({ ...formData, logo_max_width: e.target.value })} sx={{ mt: 1 }} inputProps={{ min: 50, max: 400 }} />
                                </Grid>
                            </Grid>

                            {/* Label & Input Styling */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.labelInputStyling') || 'Label & Input Styling'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.labelColor') || 'Label Color'}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input type="color" value={formData.label_color} onChange={(e) => setFormData({ ...formData, label_color: e.target.value })} style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                                        <TextField size="small" value={formData.label_color} onChange={(e) => setFormData({ ...formData, label_color: e.target.value })} sx={{ width: 100 }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.labelFontSize') || 'Label Font Size'}</Typography>
                                    <TextField fullWidth size="small" type="number" value={formData.label_font_size} onChange={(e) => setFormData({ ...formData, label_font_size: e.target.value })} sx={{ mt: 1 }} inputProps={{ min: 10, max: 32 }} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.labelFontWeight') || 'Label Font Weight'}</Typography>
                                    <TextField fullWidth size="small" select SelectProps={{ native: true }} value={formData.label_font_weight} onChange={(e) => setFormData({ ...formData, label_font_weight: e.target.value })} sx={{ mt: 1 }}>
                                        <option value="400">Normal (400)</option>
                                        <option value="500">Medium (500)</option>
                                        <option value="600">Semi-bold (600)</option>
                                        <option value="700">Bold (700)</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.inputBorderColor') || 'Input Border Color'}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input type="color" value={formData.input_border_color} onChange={(e) => setFormData({ ...formData, input_border_color: e.target.value })} style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                                        <TextField size="small" value={formData.input_border_color} onChange={(e) => setFormData({ ...formData, input_border_color: e.target.value })} sx={{ width: 100 }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.inputFocusColor') || 'Input Focus Color'}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <input type="color" value={formData.input_focus_color} onChange={(e) => setFormData({ ...formData, input_focus_color: e.target.value })} style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                                        <TextField size="small" value={formData.input_focus_color} onChange={(e) => setFormData({ ...formData, input_focus_color: e.target.value })} sx={{ width: 100 }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">{t('forms.borderRadius') || 'Border Radius (px)'}</Typography>
                                    <TextField fullWidth size="small" type="number" value={formData.border_radius} onChange={(e) => setFormData({ ...formData, border_radius: e.target.value })} sx={{ mt: 1 }} inputProps={{ min: 0, max: 50 }} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <FormControlLabel
                                        control={<Checkbox checked={!!formData.box_shadow} onChange={(e) => setFormData({ ...formData, box_shadow: e.target.checked })} />}
                                        label={t('forms.enableBoxShadow') || 'Enable Box Shadow'}
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Email Templates */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                                {t('forms.emailTemplates') || 'Email Templates'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.acknowledgmentEn') || 'Acknowledgment (EN)'} value={formData.email_acknowledgment_en} onChange={(e) => setFormData({ ...formData, email_acknowledgment_en: e.target.value })} placeholder="Thank you for submission #{submission_id}..." helperText={t('forms.templatePlaceholders') || 'Use {name}, {submission_id}'} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.acknowledgmentAr') || 'Acknowledgment (AR)'} value={formData.email_acknowledgment_ar} onChange={(e) => setFormData({ ...formData, email_acknowledgment_ar: e.target.value })} dir="rtl" placeholder="شكراً لتقديم طلبك #{submission_id}..." />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.followupEn') || 'Follow-up (EN)'} value={formData.email_followup_en} onChange={(e) => setFormData({ ...formData, email_followup_en: e.target.value })} placeholder="Following up on #{submission_id}..." />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.followupAr') || 'Follow-up (AR)'} value={formData.email_followup_ar} onChange={(e) => setFormData({ ...formData, email_followup_ar: e.target.value })} dir="rtl" placeholder="متابعة بخصوص طلبك #{submission_id}..." />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.resolutionEn') || 'Resolution (EN)'} value={formData.email_resolution_en} onChange={(e) => setFormData({ ...formData, email_resolution_en: e.target.value })} placeholder="Your inquiry #{submission_id} has been resolved..." />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth multiline rows={2} label={t('forms.resolutionAr') || 'Resolution (AR)'} value={formData.email_resolution_ar} onChange={(e) => setFormData({ ...formData, email_resolution_ar: e.target.value })} dir="rtl" placeholder="تم حل استفسارك #{submission_id}..." />
                                </Grid>
                            </Grid>

                            {/* PDF Export Settings */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                📄 {t('forms.pdfSettings') || 'PDF Export Settings'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        ⚠️ Warning Message (Red alert box shown at bottom of PDF)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Warning Message (EN)"
                                        value={formData.print_warning_en}
                                        onChange={(e) => setFormData({ ...formData, print_warning_en: e.target.value })}
                                        placeholder="This document is for official use only..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Warning Message (AR)"
                                        value={formData.print_warning_ar}
                                        onChange={(e) => setFormData({ ...formData, print_warning_ar: e.target.value })}
                                        dir="rtl"
                                        placeholder="هذه الوثيقة للاستخدام الرسمي فقط..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        ℹ️ Note Message (Blue info box shown at bottom of PDF)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Note Message (EN)"
                                        value={formData.print_note_en}
                                        onChange={(e) => setFormData({ ...formData, print_note_en: e.target.value })}
                                        placeholder="Please scan this QR code at the entrance..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Note Message (AR)"
                                        value={formData.print_note_ar}
                                        onChange={(e) => setFormData({ ...formData, print_note_ar: e.target.value })}
                                        dir="rtl"
                                        placeholder="يرجى مسح رمز QR عند المدخل..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        📝 Footer Text (Gray footer area at bottom of PDF)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Footer Text (EN)"
                                        value={formData.print_footer_en}
                                        onChange={(e) => setFormData({ ...formData, print_footer_en: e.target.value })}
                                        placeholder="Any modification to this document renders it invalid..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Footer Text (AR)"
                                        value={formData.print_footer_ar}
                                        onChange={(e) => setFormData({ ...formData, print_footer_ar: e.target.value })}
                                        dir="rtl"
                                        placeholder="أي تعديل على هذه الوثيقة يلغي صلاحيتها..."
                                    />
                                </Grid>

                                {/* Export Field Selection */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        📋 Select Fields to Show in PDF Export
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.export_all_fields !== false}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    export_all_fields: e.target.checked,
                                                    export_fields: e.target.checked ? [] : formData.fields.map(f => f.id)
                                                })}
                                            />
                                        }
                                        label="Export All Fields"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Content Layout (Columns)</InputLabel>
                                        <Select
                                            value={formData.export_columns || 1}
                                            onChange={(e) => setFormData({ ...formData, export_columns: e.target.value })}
                                            label="Content Layout (Columns)"
                                        >
                                            <MenuItem value={1}>Single Column (Default)</MenuItem>
                                            <MenuItem value={2}>Two Columns</MenuItem>
                                            <MenuItem value={3}>Three Columns</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.export_include_response || false}
                                                onChange={(e) => setFormData({ ...formData, export_include_response: e.target.checked })}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">📨 Include Email Response in PDF</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    When enabled, the admin's email response will appear in the exported PDF
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                                {formData.export_all_fields === false && (
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            bgcolor: 'grey.50',
                                            border: '1px solid',
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                            p: 2,
                                            maxHeight: 300,
                                            overflow: 'auto'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                Select which fields to include in PDF export:
                                            </Typography>
                                            {formData.fields.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    No fields added yet. Add fields in the Fields tab first.
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {formData.fields.map((field) => (
                                                        <FormControlLabel
                                                            key={field.id}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={(formData.export_fields || []).includes(field.id)}
                                                                    onChange={(e) => {
                                                                        const currentExportFields = formData.export_fields || []
                                                                        if (e.target.checked) {
                                                                            setFormData({
                                                                                ...formData,
                                                                                export_fields: [...currentExportFields, field.id]
                                                                            })
                                                                        } else {
                                                                            setFormData({
                                                                                ...formData,
                                                                                export_fields: currentExportFields.filter(id => id !== field.id)
                                                                            })
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body2">
                                                                        {field.label_en || field.label || field.id}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ({field.type})
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box sx={{ pt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('forms.formFieldsCount', { count: formData.fields.length }) || `Form Fields (${formData.fields.length})`}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => handleOpenFieldDialog()}
                                >
                                    {t('forms.addField')}
                                </Button>
                            </Box>

                            {formData.fields.length === 0 ? (
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                                    <Typography color="text.secondary">
                                        No fields added yet. Click "Add Field" to start building your form.
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {formData.fields.map((field, index) => (
                                        <Paper
                                            key={field.id || index}
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                bgcolor: 'background.default',
                                            }}
                                        >
                                            <DragIndicator color="action" sx={{ cursor: 'grab' }} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography fontWeight={500}>
                                                    {field.label_en}
                                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {field.type} • {field.id}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" onClick={() => handleMoveField(index, -1)} disabled={index === 0}>
                                                ↑
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleMoveField(index, 1)} disabled={index === formData.fields.length - 1}>
                                                ↓
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleOpenFieldDialog(field, index)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveField(index)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Users Tab - Shared login email + individual users */}
                    {activeTab === 2 && editingForm && (
                        <Box sx={{ pt: 1 }}>
                            {/* Shared Login Email Section */}
                            <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                    {language === 'ar' ? 'البريد الإلكتروني المشترك للدخول' : 'Shared Login Email'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {language === 'ar'
                                        ? 'جميع المستخدمين يستخدمون هذا البريد للدخول، ولكل مستخدم كلمة مرور خاصة به.'
                                        : 'All users login with this email. Each user has their own password for identification.'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    label={language === 'ar' ? 'البريد الإلكتروني للدخول' : 'Login Email'}
                                    value={formData.login_email || ''}
                                    onChange={(e) => setFormData({ ...formData, login_email: e.target.value })}
                                    placeholder="dashboard@example.com"
                                    type="email"
                                    size="small"
                                />
                            </Paper>

                            {/* Users Management Section */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {language === 'ar' ? `المستخدمون (${formUsers.length})` : `Form Users (${formUsers.length})`}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ContentCopy />}
                                        onClick={() => setCopyUsersDialogOpen(true)}
                                        disabled={forms.filter(f => f.id !== editingForm?.id).length === 0}
                                    >
                                        {language === 'ar' ? 'نسخ مستخدمين' : 'Copy Users'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonAdd />}
                                        onClick={() => handleOpenUserDialog()}
                                    >
                                        {language === 'ar' ? 'إضافة مستخدم' : 'Add User'}
                                    </Button>
                                </Box>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {language === 'ar'
                                    ? 'كل مستخدم لديه كلمة مرور خاصة به. عند الدخول، يتم التعرف على المستخدم من خلال كلمة المرور.'
                                    : 'Each user has their own password. When logging in, the user is identified by their password.'}
                            </Typography>

                            {formUsers.length === 0 ? (
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                                    <People sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="text.secondary">
                                        {language === 'ar'
                                            ? 'لا يوجد مستخدمين. أضف مستخدمين للسماح لهم بإدارة طلبات هذا النموذج.'
                                            : 'No users added yet. Add users to allow them to manage this form\'s submissions.'}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {formUsers.map((user) => (
                                        <Paper
                                            key={user.id}
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                bgcolor: 'background.default',
                                            }}
                                        >
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography fontWeight={500}>
                                                    {user.name_en}
                                                    {user.name_ar && <span style={{ color: '#666', marginInlineStart: 8 }}>({user.name_ar})</span>}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {language === 'ar' ? 'يسجل الدخول باستخدام كلمة مروره الخاصة' : 'Logs in using their own password'}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={user.is_active ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                                                size="small"
                                                color={user.is_active ? 'success' : 'default'}
                                            />
                                            <IconButton size="small" onClick={() => handleOpenUserDialog(user)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* WhatsApp Tab */}
                    {activeTab === 3 && editingForm && (
                        <WhatsAppSettings formId={editingForm.id} language={language} />
                    )}

                    {/* Settings Tab */}
                    {activeTab === 4 && editingForm && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                📄 {language === 'ar' ? 'إعدادات تصدير PDF' : 'PDF Export Settings'}
                            </Typography>

                            <Grid container spacing={2}>
                                {/* Export Field Selection */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        📋 {language === 'ar' ? 'اختر الحقول التي ستظهر في تصدير PDF' : 'Select Fields to Show in PDF Export'}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.export_all_fields !== false}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    export_all_fields: e.target.checked,
                                                    export_fields: e.target.checked ? [] : formData.fields.map(f => f.id)
                                                })}
                                            />
                                        }
                                        label={language === 'ar' ? 'تصدير جميع الحقول' : 'Export All Fields'}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{language === 'ar' ? 'تخطيط المحتوى (الأعمدة)' : 'Content Layout (Columns)'}</InputLabel>
                                        <Select
                                            value={formData.export_columns || 1}
                                            onChange={(e) => setFormData({ ...formData, export_columns: e.target.value })}
                                            label={language === 'ar' ? 'تخطيط المحتوى (الأعمدة)' : 'Content Layout (Columns)'}
                                        >
                                            <MenuItem value={1}>{language === 'ar' ? 'عمود واحد (افتراضي)' : 'Single Column (Default)'}</MenuItem>
                                            <MenuItem value={2}>{language === 'ar' ? 'عمودان' : 'Two Columns'}</MenuItem>
                                            <MenuItem value={3}>{language === 'ar' ? 'ثلاثة أعمدة' : 'Three Columns'}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.export_include_response || false}
                                                onChange={(e) => setFormData({ ...formData, export_include_response: e.target.checked })}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">
                                                    📨 {language === 'ar' ? 'تضمين رد البريد الإلكتروني في PDF' : 'Include Email Response in PDF'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {language === 'ar' ? 'عند التفعيل، سيظهر رد المسؤول في ملف PDF المصدّر' : "When enabled, the admin's email response will appear in the exported PDF"}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                                {formData.export_all_fields === false && (
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            bgcolor: 'grey.50',
                                            border: '1px solid',
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                            p: 2,
                                            maxHeight: 400,
                                            overflow: 'auto'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                {language === 'ar' ? 'اختر الحقول وحدد العمود لكل حقل:' : 'Select fields and assign column for each:'}
                                            </Typography>
                                            {formData.fields.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {language === 'ar' ? 'لم تتم إضافة حقول بعد. أضف حقولاً في تبويب الحقول أولاً.' : 'No fields added yet. Add fields in the Fields tab first.'}
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {formData.fields.map((field) => {
                                                        const isFieldSelected = (formData.export_fields || []).includes(field.id)
                                                        const fieldColumnAssignments = formData.export_field_columns || {}
                                                        const fieldColumn = fieldColumnAssignments[field.id] || 1
                                                        const columnCount = parseInt(formData.export_columns) || 1

                                                        return (
                                                            <Box
                                                                key={field.id}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 2,
                                                                    p: 1,
                                                                    bgcolor: isFieldSelected ? 'primary.50' : 'transparent',
                                                                    borderRadius: 1,
                                                                    border: isFieldSelected ? '1px solid' : 'none',
                                                                    borderColor: 'primary.200'
                                                                }}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={isFieldSelected}
                                                                            onChange={(e) => {
                                                                                const currentExportFields = formData.export_fields || []
                                                                                if (e.target.checked) {
                                                                                    setFormData({
                                                                                        ...formData,
                                                                                        export_fields: [...currentExportFields, field.id]
                                                                                    })
                                                                                } else {
                                                                                    // Also remove column assignment when unchecked
                                                                                    const newColumnAssignments = { ...fieldColumnAssignments }
                                                                                    delete newColumnAssignments[field.id]
                                                                                    setFormData({
                                                                                        ...formData,
                                                                                        export_fields: currentExportFields.filter(id => id !== field.id),
                                                                                        export_field_columns: newColumnAssignments
                                                                                    })
                                                                                }
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography variant="body2">
                                                                                {field.label_en || field.label || field.id}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                ({field.type})
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{ m: 0, flex: 1 }}
                                                                />
                                                                {isFieldSelected && columnCount > 1 && (
                                                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                                                        <Select
                                                                            value={fieldColumn}
                                                                            onChange={(e) => {
                                                                                setFormData({
                                                                                    ...formData,
                                                                                    export_field_columns: {
                                                                                        ...fieldColumnAssignments,
                                                                                        [field.id]: e.target.value
                                                                                    }
                                                                                })
                                                                            }}
                                                                            displayEmpty
                                                                        >
                                                                            {Array.from({ length: columnCount }, (_, i) => (
                                                                                <MenuItem key={i + 1} value={i + 1}>
                                                                                    {language === 'ar' ? `عمود ${i + 1}` : `Column ${i + 1}`}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                )}
                                                            </Box>
                                                        )
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {/* Live Chat Tab */}
                    {activeTab === 5 && editingForm && (
                        <LiveChatSettings
                            formId={editingForm.id}
                            formData={formData}
                            setFormData={setFormData}
                            language={language}
                        />
                    )}

                    {/* External API Tab */}
                    {activeTab === 6 && editingForm && (
                        <FormApiSettings
                            formId={editingForm.id}
                            formFields={formData.fields || []}
                            language={language}
                        />
                    )}

                    {/* Categories Tab */}
                    {activeTab === 7 && editingForm && (
                        <Box sx={{ pt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    🏷️ {language === 'ar' ? 'تصنيفات الطلبات' : 'Submission Categories'}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => handleOpenCategoryDialog()}
                                >
                                    {language === 'ar' ? 'إضافة تصنيف' : 'Add Category'}
                                </Button>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {language === 'ar'
                                    ? 'أنشئ تصنيفات لتنظيم وتصفية الطلبات المقدمة لهذا النموذج.'
                                    : 'Create categories to organize and filter submissions for this form.'}
                            </Typography>

                            {categoryLoading ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : formCategories.length === 0 ? (
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                                    <Label sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="text.secondary">
                                        {language === 'ar'
                                            ? 'لا توجد تصنيفات. أضف تصنيفات لتنظيم الطلبات.'
                                            : 'No categories yet. Add categories to organize submissions.'}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {formCategories.map((cat) => (
                                        <Paper
                                            key={cat.id}
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                bgcolor: 'background.default',
                                                opacity: cat.is_active ? 1 : 0.6,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: cat.color || '#1a3a6b',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography fontWeight={500}>
                                                    {language === 'ar' ? cat.name_ar : cat.name_en}
                                                    {' '}
                                                    <span style={{ color: '#666', fontSize: '0.85em' }}>
                                                        ({language === 'ar' ? cat.name_en : cat.name_ar})
                                                    </span>
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={cat.is_active
                                                    ? (language === 'ar' ? 'نشط' : 'Active')
                                                    : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                                                size="small"
                                                color={cat.is_active ? 'success' : 'default'}
                                                onClick={(e) => handleToggleCategory(e, cat)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                            <IconButton size="small" onClick={() => handleOpenCategoryDialog(cat)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleRequestDeleteCategory(cat)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Box>
                            )}

                            {/* Category Delete Confirmation Dialog */}
                            <Dialog open={deleteCategoryOpen} onClose={() => setDeleteCategoryOpen(false)}>
                                <DialogTitle>{language === 'ar' ? 'حذف تصنيف' : 'Delete Category'}</DialogTitle>
                                <DialogContent>
                                    <Typography>
                                        {language === 'ar'
                                            ? `هل أنت متأكد من حذف التصنيف "${categoryToDelete?.name_ar || ''}"؟`
                                            : `Are you sure you want to delete category "${categoryToDelete?.name_en || ''}"?`}
                                    </Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setDeleteCategoryOpen(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
                                    <Button onClick={handleConfirmDeleteCategory} color="error" variant="contained" autoFocus>
                                        {language === 'ar' ? 'حذف' : 'Delete'}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {/* Category Create/Edit Dialog */}
                            <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
                                <DialogTitle>
                                    {editingCategory
                                        ? (language === 'ar' ? 'تعديل التصنيف' : 'Edit Category')
                                        : (language === 'ar' ? 'إضافة تصنيف' : 'Add Category')}
                                </DialogTitle>
                                <DialogContent>
                                    <Grid container spacing={2} sx={{ pt: 1 }}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={language === 'ar' ? 'الاسم بالانجليزي' : 'Name (English)'}
                                                value={categoryData.name_en}
                                                onChange={(e) => setCategoryData({ ...categoryData, name_en: e.target.value })}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={language === 'ar' ? 'الاسم بالعربي' : 'Name (Arabic)'}
                                                value={categoryData.name_ar}
                                                onChange={(e) => setCategoryData({ ...categoryData, name_ar: e.target.value })}
                                                required
                                                inputProps={{ dir: 'rtl' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                {language === 'ar' ? 'اللون' : 'Color'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {PRESET_COLORS.map((c) => (
                                                    <Box
                                                        key={c}
                                                        onClick={() => setCategoryData({ ...categoryData, color: c })}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '50%',
                                                            bgcolor: c,
                                                            cursor: 'pointer',
                                                            border: categoryData.color === c ? '3px solid #333' : '2px solid transparent',
                                                            transition: 'border 0.15s',
                                                            '&:hover': { transform: 'scale(1.15)' }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label={language === 'ar' ? 'ترتيب العرض' : 'Sort Order'}
                                                value={categoryData.sort_order}
                                                onChange={(e) => setCategoryData({ ...categoryData, sort_order: parseInt(e.target.value) || 0 })}
                                            />
                                        </Grid>
                                    </Grid>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setCategoryDialogOpen(false)}>
                                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                    </Button>
                                    <Button variant="contained" onClick={handleSaveCategory} disabled={categoryLoading}>
                                        {editingCategory
                                            ? (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')
                                            : (language === 'ar' ? 'إنشاء' : 'Create')}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    )}

                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveForm}
                        disabled={createMutation.isPending || updateMutation.isPending}
                    >
                        {editingForm ? 'Save Changes' : 'Create Form'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Field Dialog */}
            <Dialog open={fieldDialogOpen} onClose={handleCloseFieldDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingField ? 'Edit Field' : 'Add Field'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Field Type</InputLabel>
                                <Select
                                    value={fieldData.type}
                                    onChange={(e) => setFieldData({ ...fieldData, type: e.target.value })}
                                    label="Field Type"
                                >
                                    {FIELD_TYPES.map((ft) => (
                                        <MenuItem key={ft.type} value={ft.type}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {ft.icon}
                                                {ft.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Label (English)"
                                value={fieldData.label_en}
                                onChange={(e) => setFieldData({ ...fieldData, label_en: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Label (Arabic)"
                                value={fieldData.label_ar}
                                onChange={(e) => setFieldData({ ...fieldData, label_ar: e.target.value })}
                                dir="rtl"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Field ID"
                                value={fieldData.id}
                                onChange={(e) => setFieldData({ ...fieldData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                helperText="Auto-generated from label if empty"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Width</InputLabel>
                                <Select
                                    value={fieldData.width}
                                    onChange={(e) => setFieldData({ ...fieldData, width: e.target.value })}
                                    label="Width"
                                >
                                    {WIDTH_OPTIONS.map((w) => (
                                        <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!fieldData.required}
                                        onChange={(e) => setFieldData({ ...fieldData, required: e.target.checked })}
                                    />
                                }
                                label="Required Field"
                            />
                        </Grid>

                        {/* Email Verification - only for email fields */}
                        {fieldData.type === 'email' && (
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={fieldData.require_verification || false}
                                            onChange={(e) => setFieldData({ ...fieldData, require_verification: e.target.checked })}
                                        />
                                    }
                                    label="Require Email Verification"
                                />
                            </Grid>
                        )}

                        {/* Placeholder fields - not for select/radio/checkbox */}
                        {!['select', 'radio', 'checkbox'].includes(fieldData.type) && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Placeholder (English)"
                                        value={fieldData.placeholder_en}
                                        onChange={(e) => setFieldData({ ...fieldData, placeholder_en: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Placeholder (Arabic)"
                                        value={fieldData.placeholder_ar}
                                        onChange={(e) => setFieldData({ ...fieldData, placeholder_ar: e.target.value })}
                                        dir="rtl"
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Min/Max Length for text fields */}
                        {['text', 'textarea', 'number', 'phone'].includes(fieldData.type) && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Length"
                                        value={fieldData.min_length}
                                        onChange={(e) => setFieldData({ ...fieldData, min_length: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Max Length"
                                        value={fieldData.max_length}
                                        onChange={(e) => setFieldData({ ...fieldData, max_length: e.target.value })}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Options Editor for select/radio/checkbox */}
                        {['select', 'radio', 'checkbox'].includes(fieldData.type) && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Options ({fieldData.options.length})
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => setFieldData({
                                            ...fieldData,
                                            options: [...fieldData.options, { value: '', label_en: '', label_ar: '' }]
                                        })}
                                    >
                                        Add Option
                                    </Button>
                                </Box>
                                {fieldData.options.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        No options added. Click "Add Option" to create choices.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {fieldData.options.map((opt, idx) => (
                                            <Paper key={idx} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid item sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                if (idx === 0) return;
                                                                const newOptions = [...fieldData.options];
                                                                [newOptions[idx - 1], newOptions[idx]] = [newOptions[idx], newOptions[idx - 1]];
                                                                setFieldData({ ...fieldData, options: newOptions });
                                                            }}
                                                            disabled={idx === 0}
                                                            sx={{ p: 0.25 }}
                                                        >
                                                            ↑
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                if (idx === fieldData.options.length - 1) return;
                                                                const newOptions = [...fieldData.options];
                                                                [newOptions[idx], newOptions[idx + 1]] = [newOptions[idx + 1], newOptions[idx]];
                                                                setFieldData({ ...fieldData, options: newOptions });
                                                            }}
                                                            disabled={idx === fieldData.options.length - 1}
                                                            sx={{ p: 0.25 }}
                                                        >
                                                            ↓
                                                        </IconButton>
                                                    </Grid>
                                                    <Grid item xs>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Label (AR)"
                                                            value={opt.label_ar}
                                                            onChange={(e) => {
                                                                const newOptions = [...fieldData.options]
                                                                newOptions[idx].label_ar = e.target.value
                                                                setFieldData({ ...fieldData, options: newOptions })
                                                            }}
                                                            dir="rtl"
                                                        />
                                                    </Grid>
                                                    <Grid item xs>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Label (EN)"
                                                            value={opt.label_en}
                                                            onChange={(e) => {
                                                                const newOptions = [...fieldData.options]
                                                                newOptions[idx].label_en = e.target.value
                                                                // Auto-generate value from label if empty
                                                                if (!newOptions[idx].value) {
                                                                    newOptions[idx].value = e.target.value.toLowerCase().replace(/\s+/g, '_')
                                                                }
                                                                setFieldData({ ...fieldData, options: newOptions })
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Value"
                                                            value={opt.value}
                                                            onChange={(e) => {
                                                                const newOptions = [...fieldData.options]
                                                                newOptions[idx].value = e.target.value.toLowerCase().replace(/\s+/g, '_')
                                                                setFieldData({ ...fieldData, options: newOptions })
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newOptions = fieldData.options.filter((_, i) => i !== idx)
                                                                setFieldData({ ...fieldData, options: newOptions })
                                                            }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Grid>
                        )}

                        {/* Repeater Settings - only for repeater type */}
                        {fieldData.type === 'repeater' && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Repeater Settings / إعدادات المكرر
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Trigger Field (count)</InputLabel>
                                            <Select
                                                value={fieldData.trigger_field || ''}
                                                onChange={(e) => setFieldData({ ...fieldData, trigger_field: e.target.value })}
                                                label="Trigger Field (count)"
                                            >
                                                {formData.fields.filter(f => f.type === 'number' && f.id !== fieldData.id).map((f) => (
                                                    <MenuItem key={f.id} value={f.id}>{f.label_en} ({f.id})</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Offset"
                                            value={fieldData.offset || 0}
                                            onChange={(e) => setFieldData({ ...fieldData, offset: parseInt(e.target.value) || 0 })}
                                            helperText="e.g., -1 for n-1"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Max Items"
                                            value={fieldData.max || 10}
                                            onChange={(e) => setFieldData({ ...fieldData, max: parseInt(e.target.value) || 10 })}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Sub-fields Editor */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Sub-fields (per item) / الحقول الفرعية
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => {
                                            const newFields = [...(fieldData.fields || [])]
                                            newFields.push({ id: '', type: 'text', label_en: '', label_ar: '', width: '50' })
                                            setFieldData({ ...fieldData, fields: newFields })
                                        }}
                                    >
                                        Add Sub-field
                                    </Button>
                                </Box>
                                {(!fieldData.fields || fieldData.fields.length === 0) ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        No sub-fields. Click "Add Sub-field" to add fields per repeater item.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {fieldData.fields.map((sf, idx) => (
                                            <Paper key={idx} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid item xs={12} sm={2}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Type</InputLabel>
                                                            <Select
                                                                value={sf.type || 'text'}
                                                                onChange={(e) => {
                                                                    const newFields = [...fieldData.fields]
                                                                    newFields[idx].type = e.target.value
                                                                    setFieldData({ ...fieldData, fields: newFields })
                                                                }}
                                                                label="Type"
                                                            >
                                                                <MenuItem value="text">Text</MenuItem>
                                                                <MenuItem value="phone">Phone</MenuItem>
                                                                <MenuItem value="email">Email</MenuItem>
                                                                <MenuItem value="file">File</MenuItem>
                                                                <MenuItem value="select">Select</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Label (EN)"
                                                            value={sf.label_en || ''}
                                                            onChange={(e) => {
                                                                const newFields = [...fieldData.fields]
                                                                newFields[idx].label_en = e.target.value
                                                                if (!newFields[idx].id) {
                                                                    newFields[idx].id = e.target.value.toLowerCase().replace(/\s+/g, '_')
                                                                }
                                                                setFieldData({ ...fieldData, fields: newFields })
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Label (AR)"
                                                            value={sf.label_ar || ''}
                                                            onChange={(e) => {
                                                                const newFields = [...fieldData.fields]
                                                                newFields[idx].label_ar = e.target.value
                                                                setFieldData({ ...fieldData, fields: newFields })
                                                            }}
                                                            dir="rtl"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={2}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="ID"
                                                            value={sf.id || ''}
                                                            onChange={(e) => {
                                                                const newFields = [...fieldData.fields]
                                                                newFields[idx].id = e.target.value.toLowerCase().replace(/\s+/g, '_')
                                                                setFieldData({ ...fieldData, fields: newFields })
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4} sm={1}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="W%"
                                                            type="number"
                                                            value={sf.width || '50'}
                                                            onChange={(e) => {
                                                                const newFields = [...fieldData.fields]
                                                                newFields[idx].width = e.target.value
                                                                setFieldData({ ...fieldData, fields: newFields })
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={2} sm={1}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newFields = fieldData.fields.filter((_, i) => i !== idx)
                                                                setFieldData({ ...fieldData, fields: newFields })
                                                            }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Grid>
                        )}

                        {/* Conditional Visibility Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!fieldData.conditionalVisibility}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setFieldData({
                                                        ...fieldData,
                                                        conditionalVisibility: { field: '', showWhen: '' }
                                                    })
                                                } else {
                                                    const { conditionalVisibility, ...rest } = fieldData
                                                    setFieldData(rest)
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography fontWeight={600}>
                                            Conditional Visibility / الظهور الشرطي
                                        </Typography>
                                    }
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    Show this field only when another field has a specific value
                                </Typography>

                                {fieldData.conditionalVisibility && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Show when field / أظهر عندما يكون الحقل</InputLabel>
                                                <Select
                                                    value={fieldData.conditionalVisibility.field || ''}
                                                    onChange={(e) => setFieldData({
                                                        ...fieldData,
                                                        conditionalVisibility: {
                                                            ...fieldData.conditionalVisibility,
                                                            field: e.target.value
                                                        }
                                                    })}
                                                    label="Show when field / أظهر عندما يكون الحقل"
                                                >
                                                    {formData.fields
                                                        .filter(f => f.id !== fieldData.id && ['select', 'radio', 'checkbox'].includes(f.type))
                                                        .map((f) => (
                                                            <MenuItem key={f.id} value={f.id}>
                                                                {f.label_en || f.id} ({f.type})
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {fieldData.conditionalVisibility.field && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Equals value / يساوي القيمة</InputLabel>
                                                    <Select
                                                        multiple
                                                        value={Array.isArray(fieldData.conditionalVisibility.showWhen)
                                                            ? fieldData.conditionalVisibility.showWhen
                                                            : fieldData.conditionalVisibility.showWhen
                                                                ? [fieldData.conditionalVisibility.showWhen]
                                                                : []}
                                                        onChange={(e) => {
                                                            const values = e.target.value
                                                            setFieldData({
                                                                ...fieldData,
                                                                conditionalVisibility: {
                                                                    ...fieldData.conditionalVisibility,
                                                                    showWhen: values.length === 1 ? values[0] : values
                                                                }
                                                            })
                                                        }}
                                                        label="Equals value / يساوي القيمة"
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((value) => (
                                                                    <Chip key={value} label={value} size="small" />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    >
                                                        {formData.fields
                                                            .find(f => f.id === fieldData.conditionalVisibility.field)
                                                            ?.options?.map((opt) => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    {opt.label_en || opt.value}
                                                                </MenuItem>
                                                            ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        </Grid>

                        {/* Allow Other Option - for select fields */}
                        {fieldData.type === 'select' && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!fieldData.allowOther}
                                            onChange={(e) => setFieldData({ ...fieldData, allowOther: e.target.checked })}
                                        />
                                    }
                                    label="Allow 'Other' option with text input / السماح بخيار 'أخرى' مع حقل نص"
                                />
                                {fieldData.allowOther && (
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Other label (EN)"
                                                value={fieldData.otherLabel_en || 'Please specify'}
                                                onChange={(e) => setFieldData({ ...fieldData, otherLabel_en: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Other label (AR)"
                                                value={fieldData.otherLabel_ar || 'يرجى التحديد'}
                                                onChange={(e) => setFieldData({ ...fieldData, otherLabel_ar: e.target.value })}
                                                dir="rtl"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseFieldDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveField}>
                        {editingField ? 'Save Field' : 'Add Field'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Embed Code Dialog */}
            <Dialog
                open={embedDialogOpen}
                onClose={() => setEmbedDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Code color="primary" />
                    Embed Form
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Use the appropriate embed code based on your page language.
                    </Typography>

                    {/* English Embed Code */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        🇬🇧 English Embed Code
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            bgcolor: 'grey.900',
                            borderRadius: 2,
                            position: 'relative',
                            mb: 3,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => copyEmbedCode('en')}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'grey.400',
                                '&:hover': { color: 'white' },
                            }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                        <Typography
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                color: 'grey.300',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                m: 0,
                                pr: 4,
                            }}
                        >
                            {getEmbedCode('en')}
                        </Typography>
                    </Paper>

                    {/* Arabic Embed Code */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        🇸🇦 Arabic Embed Code (للصفحات العربية)
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            bgcolor: 'grey.900',
                            borderRadius: 2,
                            position: 'relative',
                            mb: 2,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => copyEmbedCode('ar')}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'grey.400',
                                '&:hover': { color: 'white' },
                            }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                        <Typography
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                color: 'grey.300',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                m: 0,
                                pr: 4,
                            }}
                        >
                            {getEmbedCode('ar')}
                        </Typography>
                    </Paper>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        <strong>Direct Links:</strong><br />
                        English: <a href={`${window.location.origin}/form/${embedFormId}?lang=en`} target="_blank" rel="noopener noreferrer" style={{ color: '#1a3a6b' }}>{`${window.location.origin}/form/${embedFormId}?lang=en`}</a><br />
                        Arabic: <a href={`${window.location.origin}/form/${embedFormId}?lang=ar`} target="_blank" rel="noopener noreferrer" style={{ color: '#1a3a6b' }}>{`${window.location.origin}/form/${embedFormId}?lang=ar`}</a>
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEmbedDialogOpen(false)}>Close</Button>
                    <Button variant="outlined" startIcon={<ContentCopy />} onClick={() => copyEmbedCode('en')}>
                        Copy English
                    </Button>
                    <Button variant="contained" startIcon={<ContentCopy />} onClick={() => copyEmbedCode('ar')}>
                        Copy Arabic
                    </Button>
                </DialogActions>
            </Dialog>

            {/* User Dialog - Add/Edit Form User */}
            <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAdd color="primary" />
                    {editingUser ? 'Edit User' : 'Add User'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {editingUser
                            ? 'Update user information. Leave password empty to keep the current password.'
                            : 'Add a new user who can manage submissions for this form.'}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Name (English)"
                                value={userData.name_en}
                                onChange={(e) => setUserData({ ...userData, name_en: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Name (Arabic)"
                                value={userData.name_ar}
                                onChange={(e) => setUserData({ ...userData, name_ar: e.target.value })}
                                dir="rtl"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={editingForm?.login_email || ''}
                                InputProps={{ readOnly: true }}
                                disabled
                                helperText="User will log in with Form ID + this email"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={editingUser ? "New Password (leave empty to keep current)" : "Password"}
                                type="password"
                                value={userData.password}
                                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                required={!editingUser}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!userData.is_active}
                                        onChange={(e) => setUserData({ ...userData, is_active: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseUserDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveUser}
                        disabled={createUserMutation.isPending || updateUserMutation.isPending}
                    >
                        {editingUser ? 'Save Changes' : 'Add User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Template Selector Dialog */}
            <Dialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        📋 Choose a Form Template
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Select a template to quickly create a pre-configured form with professional fields and styling.
                    </Typography>
                    <Grid container spacing={2}>
                        {FORM_TEMPLATES.map((template) => (
                            <Grid item xs={12} md={6} key={template.id}>
                                <Paper
                                    sx={{
                                        p: 3,
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                    onClick={() => handleUseTemplate(template)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Typography variant="h4" sx={{ lineHeight: 1 }}>
                                            {template.icon}
                                        </Typography>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {language === 'ar' ? template.name_ar : template.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {template.data.fields.length} fields
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {template.description}
                                    </Typography>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 1,
                                                background: `linear-gradient(135deg, ${template.data.primary_color} 0%, ${template.data.header_gradient_end} 100%)`
                                            }}
                                        />
                                        <Chip
                                            label={template.data.submission_prefix}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                    <Button variant="outlined" onClick={() => { setTemplateDialogOpen(false); handleOpenDialog(); }}>
                        Start from Scratch
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Form Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <Delete color="error" />
                    {language === 'ar' ? 'تأكيد حذف النموذج' : 'Confirm Form Deletion'}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'تحذير: حذف هذا النموذج سيؤدي إلى حذف جميع الطلبات والبيانات المرتبطة به بشكل دائم!'
                            : 'Warning: Deleting this form will permanently delete all submissions and related data!'}
                    </Alert>

                    <Typography variant="h6" gutterBottom>
                        {language === 'ar' ? formToDelete?.title_ar || formToDelete?.title : formToDelete?.title}
                    </Typography>

                    {isLoadingDeleteUsers ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : deleteFormUsers.length > 0 ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography fontWeight={600}>
                                    {language === 'ar'
                                        ? `هذا النموذج يحتوي على ${deleteFormUsers.length} مستخدم/مستخدمين!`
                                        : `This form has ${deleteFormUsers.length} dashboard user(s)!`}
                                </Typography>
                                <Typography variant="body2">
                                    {language === 'ar'
                                        ? 'يمكنك نقل المستخدمين إلى نموذج آخر قبل الحذف'
                                        : 'You can migrate users to another form before deletion'}
                                </Typography>
                            </Alert>

                            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2, mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {language === 'ar' ? 'المستخدمون المتأثرون:' : 'Affected Users:'}
                                </Typography>
                                {deleteFormUsers.map(user => (
                                    <Chip
                                        key={user.id}
                                        label={language === 'ar' ? user.name_ar || user.name_en : user.name_en}
                                        size="small"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ))}
                            </Box>

                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>
                                    {language === 'ar' ? 'نقل المستخدمين إلى نموذج آخر (اختياري)' : 'Migrate users to another form (Optional)'}
                                </InputLabel>
                                <Select
                                    value={migrateUsersTo}
                                    onChange={(e) => setMigrateUsersTo(e.target.value)}
                                    label={language === 'ar' ? 'نقل المستخدمين إلى نموذج آخر (اختياري)' : 'Migrate users to another form (Optional)'}
                                >
                                    <MenuItem value="">
                                        <em>{language === 'ar' ? 'لا تنقل - حذف المستخدمين مع النموذج' : "Don't migrate - Delete users with form"}</em>
                                    </MenuItem>
                                    {forms
                                        .filter(f => f.id !== formToDelete?.id)
                                        .map(form => (
                                            <MenuItem key={form.id} value={form.id}>
                                                {language === 'ar' ? form.title_ar || form.title : form.title}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        </Box>
                    ) : (
                        <Typography color="text.secondary" sx={{ mt: 2 }}>
                            {language === 'ar'
                                ? 'لا يوجد مستخدمون مرتبطون بهذا النموذج'
                                : 'No users are associated with this form'}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelDelete}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        startIcon={<Delete />}
                    >
                        {migrateUsersTo && deleteFormUsers.length > 0
                            ? (language === 'ar' ? 'نقل المستخدمين ثم الحذف' : 'Migrate Users & Delete')
                            : (language === 'ar' ? 'حذف النموذج' : 'Delete Form')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Users Dialog */}
            <Dialog
                open={copyUsersDialogOpen}
                onClose={() => { setCopyUsersDialogOpen(false); setCopyFromFormId(''); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ContentCopy color="primary" />
                    {language === 'ar' ? 'نسخ مستخدمين من نموذج آخر' : 'Copy Users from Another Form'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'اختر نموذجاً لنسخ مستخدميه إلى هذا النموذج. سيتم تخطي المستخدمين الموجودين بالفعل (بنفس البريد الإلكتروني).'
                            : 'Select a form to copy its users to this form. Users with duplicate emails will be skipped.'}
                    </Typography>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>
                            {language === 'ar' ? 'نسخ مستخدمين من' : 'Copy users from'}
                        </InputLabel>
                        <Select
                            value={copyFromFormId}
                            onChange={(e) => setCopyFromFormId(e.target.value)}
                            label={language === 'ar' ? 'نسخ مستخدمين من' : 'Copy users from'}
                        >
                            {forms
                                .filter(f => f.id !== editingForm?.id)
                                .map(form => (
                                    <MenuItem key={form.id} value={form.id}>
                                        {language === 'ar' ? form.title_ar || form.title : form.title}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => { setCopyUsersDialogOpen(false); setCopyFromFormId(''); }}
                        disabled={isCopyingUsers}
                    >
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCopyUsers}
                        disabled={!copyFromFormId || isCopyingUsers}
                        startIcon={isCopyingUsers ? <CircularProgress size={16} color="inherit" /> : <ContentCopy />}
                    >
                        {isCopyingUsers
                            ? (language === 'ar' ? 'جاري النسخ...' : 'Copying...')
                            : (language === 'ar' ? 'نسخ المستخدمين' : 'Copy Users')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDeleteUser.open}
                title={language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                message={
                    language === 'ar'
                        ? `هل أنت متأكد من حذف المستخدم "${confirmDeleteUser.name}"؟`
                        : `Are you sure you want to delete user "${confirmDeleteUser.name}"?`
                }
                confirmText={language === 'ar' ? 'حذف' : 'Delete'}
                cancelText={language === 'ar' ? 'إلغاء' : 'Cancel'}
                onConfirm={handleConfirmDeleteUser}
                onCancel={() => setConfirmDeleteUser({ open: false, userId: null, name: '' })}
            />
        </Box>
    )
}

export default Forms
